// Generated from hara_reading. Keep release 1.0.0 content immutable.
(() => {
const ID = /^[a-z][a-z0-9-]*$/;
const VERSION = /^\d+\.\d+\.\d+$/;
const owns = (object, key) => Object.hasOwn(object, key);
const record = value => value !== null && typeof value === 'object' && !Array.isArray(value)
  && [Object.prototype, null].includes(Object.getPrototypeOf(value));
const clone = value => JSON.parse(JSON.stringify(value));
const freeze = value => {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
  return value;
};

class ReadingError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'ReadingError';
    this.code = code;
  }
}

function requireValue(condition, message) {
  if (!condition) throw new ReadingError('INVALID_CONTENT', message);
}

const nonempty = value => typeof value === 'string' && value.trim().length > 0;
const validId = value => typeof value === 'string' && ID.test(value);
const unique = values => new Set(values).size === values.length;

function combinations(questions) {
  return questions.reduce((paths, question) => paths.flatMap(path =>
    question.answers.map(answer => ({ ...path, [question.id]: answer.id }))), [{}]);
}

function matchingSpreads(bundle, answers) {
  return bundle.spreads.filter(spread => Object.entries(spread.when)
    .every(([question, answer]) => answers[question] === answer));
}

/** Validate the complete content graph, including every possible answer path. */
function validateBundle(bundle) {
  requireValue(record(bundle), 'A content bundle must be an object.');
  requireValue(bundle.schemaVersion === 1, 'Unsupported content schema.');
  requireValue(validId(bundle.experienceId), 'Invalid experience ID.');
  requireValue(typeof bundle.contentVersion === 'string' && VERSION.test(bundle.contentVersion), 'Invalid content version.');
  requireValue(nonempty(bundle.locale), 'Missing locale.');
  if (bundle.presentation !== undefined) requireValue(record(bundle.presentation)
    && bundle.presentation.mode === 'drawn' && bundle.presentation.drawSize === 3, 'Invalid presentation.');
  if (bundle.presentation?.poolSize !== undefined) requireValue(Number.isInteger(bundle.presentation.poolSize) && bundle.presentation.poolSize >= 3 && bundle.presentation.poolSize <= 12, 'Invalid pool size.');
  requireValue(Array.isArray(bundle.questions) && bundle.questions.length >= 1 && bundle.questions.length <= 6,
    'A bundle must have one to six questions.');
  const questionIds = bundle.questions.map(question => question?.id);
  requireValue(questionIds.every(validId) && unique(questionIds), 'Question IDs must be unique slugs.');
  for (const key of ['assets', 'cards', 'readings', 'everyday']) {
    requireValue(record(bundle[key]) && Object.keys(bundle[key]).length > 0, `Missing ${key}.`);
    requireValue(Object.keys(bundle[key]).every(validId), `Invalid ${key} ID.`);
  }
  for (const [id, asset] of Object.entries(bundle.assets)) {
    requireValue(record(asset) && typeof asset.src === 'string'
      && /^assets\/[0-9]+\.[0-9]+\.[0-9]+\/[a-z][a-z0-9-]*\.png$/.test(asset.src), `Invalid asset path: ${id}.`);
    requireValue(asset.src.startsWith(`assets/${bundle.contentVersion}/`), `Asset version mismatch: ${id}.`);
    requireValue(nonempty(asset.alt), `Missing neutral image description: ${id}.`);
    requireValue(Number.isInteger(asset.width) && asset.width > 0 && Number.isInteger(asset.height) && asset.height > 0,
      `Invalid dimensions: ${id}.`);
    requireValue(asset.mimeType === 'image/png', `Unsupported source asset format: ${id}.`);
  }
  for (const question of bundle.questions) {
    requireValue(nonempty(question.prompt), `Missing prompt: ${question.id}.`);
    requireValue(Array.isArray(question.answers) && question.answers.length === 2, `Expected two answers: ${question.id}.`);
    const ids = question.answers.map(answer => answer?.id);
    requireValue(ids.every(validId) && unique(ids), `Invalid answer IDs: ${question.id}.`);
    for (const answer of question.answers) {
      requireValue(nonempty(answer.label) && validId(answer.assetId) && owns(bundle.assets, answer.assetId), `Invalid answer: ${question.id}/${answer.id}.`);
      if (answer.assetIds !== undefined) requireValue(Array.isArray(answer.assetIds) && answer.assetIds.length === 2
        && unique(answer.assetIds) && answer.assetIds.includes(answer.assetId)
        && answer.assetIds.every(id => validId(id) && owns(bundle.assets, id)), `Invalid answer artwork: ${question.id}/${answer.id}.`);
    }
  }
  for (const [id, card] of Object.entries(bundle.cards)) {
    requireValue(record(card) && nonempty(card.name) && validId(card.assetId) && owns(bundle.assets, card.assetId), `Invalid card: ${id}.`);
  }
  const invitationQuestion = bundle.questions.find(q => q.id === bundle.invitationQuestionId);
  const everydayQuestion = bundle.questions.find(q => q.id === bundle.everydayQuestionId);
  requireValue(!!invitationQuestion && !!everydayQuestion, 'Missing invitation or clothing question.');
  const hasExactKeys = (object, ids) => record(object) && Object.keys(object).length === ids.length
    && ids.every(id => owns(object, id) && nonempty(object[id]));
  requireValue(hasExactKeys(bundle.everyday, everydayQuestion.answers.map(a => a.id)), 'Incomplete clothing copy.');
  for (const [id, reading] of Object.entries(bundle.readings)) {
    requireValue(record(reading) && validId(reading.cardId) && owns(bundle.cards, reading.cardId), `Missing card for reading: ${id}.`);
    requireValue(nonempty(reading.title) && nonempty(reading.body), `Missing reading copy: ${id}.`);
    requireValue(hasExactKeys(reading.invitations, invitationQuestion.answers.map(a => a.id)), `Incomplete invitations: ${id}.`);
  }
  requireValue(Array.isArray(bundle.spreads) && bundle.spreads.length > 0, 'Missing spreads.');
  const spreadIds = bundle.spreads.map(s => s?.id);
  requireValue(spreadIds.every(validId) && unique(spreadIds), 'Spread IDs must be unique slugs.');
  const referencedReadings = new Set();
  for (const spread of bundle.spreads) {
    requireValue(record(spread.when) && Object.keys(spread.when).length > 0, `Missing spread rule: ${spread.id}.`);
    for (const [qid, aid] of Object.entries(spread.when)) {
      requireValue(bundle.questions.some(q => q.id === qid && q.answers.some(a => a.id === aid)), `Invalid spread rule: ${spread.id}.`);
    }
    requireValue(Array.isArray(spread.readingIds) && spread.readingIds.length === (bundle.presentation?.mode === 'drawn' ? (bundle.presentation.poolSize ?? 6) : 3)
      && unique(spread.readingIds) && spread.readingIds.every(id => validId(id) && owns(bundle.readings, id)), `Invalid spread readings: ${spread.id}.`);
    requireValue(unique(spread.readingIds.map(id => bundle.cards[bundle.readings[id].cardId].assetId)), `Duplicate image in spread: ${spread.id}.`);
    spread.readingIds.forEach(id => referencedReadings.add(id));
  }
  requireValue(Object.keys(bundle.readings).every(id => referencedReadings.has(id)), 'Unreachable reading.');
  requireValue(Object.keys(bundle.cards).every(id => Object.values(bundle.readings).some(r => r.cardId === id)), 'Unreachable final card.');
  for (const answers of combinations(bundle.questions)) {
    requireValue(matchingSpreads(bundle, answers).length === 1, 'Every answer path must match exactly one spread.');
  }
  requireValue(Array.isArray(bundle.products) && bundle.products.length > 0, 'Missing browsing products.');
  requireValue(unique(bundle.products.map(p => p?.handle)), 'Duplicate product handle.');
  for (const product of bundle.products) {
    requireValue(record(product) && validId(product.handle) && validId(product.category)
      && nonempty(product.title) && Array.isArray(product.shape) && product.shape.length > 0
      && product.shape.every(nonempty), 'Invalid product reference.');
  }
  requireValue(record(bundle.recovery) && ['title', 'message', 'actionLabel'].every(k => nonempty(bundle.recovery[k])),
    'Missing recovery copy.');
  return true;
}

function validateAnswers(bundle, answers, complete) {
  if (!record(answers)) throw new ReadingError('INVALID_ANSWERS', 'Answers must be an object.');
  const questions = new Map(bundle.questions.map(q => [q.id, q]));
  for (const [id, value] of Object.entries(answers)) {
    if (!questions.has(id) || !questions.get(id).answers.some(a => a.id === value)) {
      throw new ReadingError('INVALID_ANSWERS', 'An answer is not recognised.');
    }
  }
  if (complete && bundle.questions.some(q => !owns(answers, q.id))) {
    throw new ReadingError('INCOMPLETE_ANSWERS', 'Complete the questions before choosing a final card.');
  }
}

/** Pure browser/server library. Content is supplied by the caller, without network or storage side effects. */
function createReadingLibrary(bundles, options = {}) {
  requireValue(Array.isArray(bundles) && bundles.length > 0, 'Supply at least one content version.');
  bundles.forEach(validateBundle);
  requireValue(unique(bundles.map(b => b.contentVersion)), 'Duplicate content version.');
  requireValue(new Set(bundles.map(b => b.experienceId)).size === 1, 'Do not mix different experiences.');
  requireValue(bundles.length === 1 || nonempty(options.defaultVersion), 'Choose a default when loading multiple versions.');
  const versions = new Map(bundles.map(b => [b.contentVersion, freeze(clone(b))]));
  const defaultVersion = options.defaultVersion ?? bundles[0].contentVersion;
  requireValue(versions.has(defaultVersion), 'Default version is not loaded.');
  const experienceId = bundles[0].experienceId;

  function getBundle(version = defaultVersion) {
    if (!versions.has(version)) throw new ReadingError('UNKNOWN_VERSION', 'That reading version is not available.');
    return versions.get(version);
  }

  function getQuestions(version) {
    const bundle = getBundle(version);
    return freeze(bundle.questions.map(q => ({ id: q.id, prompt: q.prompt, answers: q.answers.map(a =>
      ({ id: a.id, label: a.label, image: { id: a.assetId, ...bundle.assets[a.assetId] },
        ...(a.assetIds ? { imageOptions: a.assetIds.map(id => ({ id, ...bundle.assets[id] })) } : {}) })) })));
  }

  function getSpread(answers, version) {
    const bundle = getBundle(version);
    validateAnswers(bundle, answers, false);
    const routingKeys = new Set(bundle.spreads.flatMap(s => Object.keys(s.when)));
    if ([...routingKeys].some(k => !owns(answers, k))) {
      throw new ReadingError('INCOMPLETE_ANSWERS', 'More choices are needed before showing the final spread.');
    }
    const spread = matchingSpreads(bundle, answers)[0];
    return freeze({ id: spread.id, contentVersion: bundle.contentVersion,
      choices: spread.readingIds.map(id => {
        const cardId = bundle.readings[id].cardId, card = bundle.cards[cardId];
        return { cardId, image: { id: card.assetId, ...bundle.assets[card.assetId] } };
      }) });
  }

  function resolve(input) {
    if (!record(input)) throw new ReadingError('INVALID_INPUT', 'Supply answers and a card choice.');
    const bundle = getBundle(input.contentVersion);
    validateAnswers(bundle, input.answers, true);
    const spread = matchingSpreads(bundle, input.answers)[0];
    const readingId = spread.readingIds.find(id => bundle.readings[id].cardId === input.cardId);
    if (!readingId) throw new ReadingError('CARD_NOT_OFFERED', 'Choose a card from the current spread.');
    const reading = bundle.readings[readingId], card = bundle.cards[reading.cardId];
    const answers = Object.fromEntries(bundle.questions.map(q => [q.id, input.answers[q.id]]));
    const outcomeId = `${experienceId}@${bundle.contentVersion}/${Object.values(answers).join('.')}/${reading.cardId}`;
    return freeze({ schemaVersion: 1, experienceId, contentVersion: bundle.contentVersion, outcomeId,
      locale: bundle.locale, answers, spreadId: spread.id, readingId,
      choices: bundle.questions.map(q => ({ questionId: q.id, answerId: answers[q.id],
        label: q.answers.find(a => a.id === answers[q.id]).label })),
      card: { id: reading.cardId, name: card.name, image: { id: card.assetId, ...bundle.assets[card.assetId] } },
      title: reading.title, reading: reading.body,
      invitation: reading.invitations[answers[bundle.invitationQuestionId]],
      everyday: bundle.everyday[answers[bundle.everydayQuestionId]],
      products: clone(bundle.products) });
  }

  function resolveOutcomeId(outcomeId) {
    if (typeof outcomeId !== 'string' || outcomeId.length > 512) {
      throw new ReadingError('INVALID_OUTCOME_ID', 'That reading link is not valid.');
    }
    const match = /^([a-z][a-z0-9-]*)@(\d+\.\d+\.\d+)\/([a-z0-9.-]+)\/([a-z][a-z0-9-]*)$/.exec(outcomeId);
    if (!match || match[1] !== experienceId) throw new ReadingError('INVALID_OUTCOME_ID', 'That reading link is not valid.');
    const bundle = getBundle(match[2]), ids = match[3].split('.');
    if (ids.length !== bundle.questions.length) throw new ReadingError('INVALID_OUTCOME_ID', 'That reading link is incomplete.');
    return resolve({ contentVersion: bundle.contentVersion,
      answers: Object.fromEntries(bundle.questions.map((q, i) => [q.id, ids[i]])), cardId: match[4] });
  }

  function recover(action) {
    try { return { ok: true, result: action() }; }
    catch (error) {
      if (!(error instanceof ReadingError)) throw error;
      return { ok: false, error: { code: error.code }, recovery: clone(getBundle().recovery) };
    }
  }

  function listOutcomeIds(version) {
    const bundle = getBundle(version);
    return freeze(combinations(bundle.questions).flatMap(answers => getSpread(answers, bundle.contentVersion).choices
      .map(choice => resolve({ contentVersion: bundle.contentVersion, answers, cardId: choice.cardId }).outcomeId)));
  }

  return Object.freeze({ defaultVersion, getQuestions, getSpread, resolve, resolveOutcomeId, listOutcomeIds,
    tryResolve: input => recover(() => resolve(input)),
    tryResolveOutcomeId: id => recover(() => resolveOutcomeId(id)) });
}

/** Browser-independent journey state. Stored values and history entries are untrusted. */
const STORAGE_KEY = 'hara-reading:journey:v1';
function createJourney(library) {
  const initial = () => ({ schema: 1, contentVersion: library.defaultVersion, step: 0, answers: {}, cardId: null });
  function restore(value) {
    try {
      if (!value || value.schema !== 1) return initial();
      const questions = library.getQuestions(value.contentVersion);
      if (typeof value.contentVersion !== 'string' || !Number.isInteger(value.step) || value.step < 0 || value.step > 7) return initial();
      if (!value.answers || typeof value.answers !== 'object' || Array.isArray(value.answers)) return initial();
      const answers = {};
      let missing = false;
      for (const q of questions) {
        if (value.answers[q.id] === undefined) { missing = true; continue; }
        if (missing || !q.answers.some(a => a.id === value.answers[q.id])) return initial();
        answers[q.id] = value.answers[q.id];
      }
      if (Object.keys(answers).length !== Object.keys(value.answers).length) return initial();
      const frontier = Math.min(5, Object.keys(answers).length + 1);
      if (value.step > frontier && value.step < 6) return initial();
      let cardId = null;
      if (value.cardId != null) {
        const resolved = library.tryResolve({ answers, cardId: value.cardId, contentVersion: value.contentVersion });
        if (!resolved.ok) return initial();
        cardId = value.cardId;
      }
      if (value.step >= 6 && !cardId) return initial();
      return { schema: 1, contentVersion: value.contentVersion, step: value.step, answers, cardId };
    } catch { return initial(); }
  }
  function fromOutcome(id) {
    const resolved = library.tryResolveOutcomeId(id);
    return resolved.ok ? { schema: 1, contentVersion: resolved.result.contentVersion, step: 7,
      answers: { ...resolved.result.answers }, cardId: resolved.result.card.id } : null;
  }
  function transition(current, action) {
    const state = restore(current);
    const questions = library.getQuestions(state.contentVersion);
    if (action.type === 'restart') return initial();
    if (action.type === 'begin' && state.step === 0) return { ...state, step: 1 };
    if (action.type === 'back') return { ...state, step: Math.max(0, state.step - 1) };
    if (action.type === 'edit' && state.step === 7) return { ...state, step: 1 };
    if (action.type === 'redraw' && state.step >= 6) return { ...state, step: 5, cardId: null };
    if (action.type === 'read' && state.step === 6) return { ...state, step: 7 };
    if (action.type === 'answer' && state.step >= 1 && state.step <= 4) {
      const question = questions[state.step - 1];
      if (!question.answers.some(a => a.id === action.id)) return state;
      // Retain explicit later answers for review, but always make a fresh final draw after a change.
      const changed = state.answers[question.id] !== action.id;
      return { ...state, step: state.step + 1, answers: { ...state.answers, [question.id]: action.id },
        cardId: changed ? null : state.cardId };
    }
    if (action.type === 'choose' && state.step === 5) {
      const resolved = library.tryResolve({ ...state, cardId: action.id });
      if (resolved.ok) return { ...state, step: 6, cardId: action.id };
    }
    return state;
  }
  return { initial, restore, fromOutcome, transition };
}
function readStored(storage, journey, key = STORAGE_KEY) {
  try { return journey.restore(JSON.parse(storage.getItem(key))); }
  catch { return journey.initial(); }
}
function saveStored(storage, state, key = STORAGE_KEY) {
  try { storage.setItem(key, JSON.stringify(state)); return true; }
  catch { return false; }
}
function readingUrl(base, outcomeId) {
  const url = new URL(base);
  url.search = ''; url.hash = '';
  url.searchParams.set('reading', outcomeId);
  return url.href;
}


const DRAW_LAYOUTS = Object.freeze(['row', 'cascade', 'fan', 'arc']);

/** Randomness belongs to the deal, never to the meaning of a chosen card. */
function createDrawJourney(library, bundle, { random = Math.random } = {}) {
  const base = createJourney(library);
  const version = bundle.contentVersion;
  const questions = library.getQuestions(version);
  const pools = Object.fromEntries(bundle.spreads.map(s => [s.id,
    s.readingIds.map(id => bundle.readings[id].cardId)]));
  function shuffle(items) {
    const copy = [...items];
    for (let i = copy.length - 1; i > 0; i--) {
      const sample = random();
      if (!Number.isFinite(sample) || sample < 0 || sample >= 1) throw new Error('Invalid random source');
      const j = Math.floor(sample * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }
  function deal() {
    return {
      questions: Object.fromEntries(questions.map(q => [q.id, shuffle(q.answers.map(a => ({
        id: a.id, assetId: shuffle(a.imageOptions || [a.image])[0].id
      })))])),
      pools: Object.fromEntries(Object.entries(pools).map(([id, cards]) => [id, shuffle(cards)])),
      layout: shuffle(DRAW_LAYOUTS)[0]
    };
  }
  const initial = () => ({ ...base.initial(), schema: 2, contentVersion: version, deal: deal() });
  const sameMembers = (a, b) => Array.isArray(a) && a.length === b.length
    && new Set(a).size === b.length && a.every(id => b.includes(id));
  function validDeal(value) {
    if (!value?.questions || !value?.pools) return false;
    if (!sameMembers(Object.keys(value.questions), questions.map(q => q.id))
      || !sameMembers(Object.keys(value.pools), Object.keys(pools))) return false;
    return questions.every(q => {
      const offered = value.questions[q.id];
      return Array.isArray(offered) && sameMembers(offered.map(a => a?.id), q.answers.map(a => a.id))
        && offered.every(a => (q.answers.find(x => x.id === a.id).imageOptions || []).some(img => img.id === a.assetId));
    }) && Object.entries(pools).every(([id, cards]) => sameMembers(value.pools[id], cards));
  }
  function restore(value) {
    try {
      if (value?.schema !== 2 || value.contentVersion !== version || !validDeal(value.deal)) return initial();
      const state = base.restore({ ...value, schema: 1 });
      // A reset by the base validator must never retain invalid deal/answer state.
      if (state.step !== value.step || state.contentVersion !== version
        || JSON.stringify(state.answers) !== JSON.stringify(value.answers) || state.cardId !== value.cardId) return initial();
      if (state.cardId && !value.deal.pools[library.getSpread(state.answers, version).id].slice(0, 3).includes(state.cardId)) return initial();
      const restoredDeal = JSON.parse(JSON.stringify(value.deal));
      // Older saved deals keep their cards and use the original straight row.
      if (!DRAW_LAYOUTS.includes(restoredDeal.layout)) restoredDeal.layout = 'row';
      return { ...state, schema: 2, deal: restoredDeal };
    } catch { return initial(); }
  }
  function getQuestions(state) {
    return questions.map(q => ({ ...q, answers: state.deal.questions[q.id].map(item => {
      const answer = q.answers.find(a => a.id === item.id);
      return { ...answer, image: answer.imageOptions.find(image => image.id === item.assetId) };
    }) }));
  }
  function getSpread(state) {
    const spread = library.getSpread(state.answers, version);
    return { ...spread, choices: state.deal.pools[spread.id].slice(0, 3)
      .map(id => spread.choices.find(c => c.cardId === id)) };
  }
  function transition(current, action) {
    const state = restore(current);
    if (action.type === 'restart') return initial();
    if (action.type === 'deal' && state.step >= 5) {
      const spread = getSpread(state);
      const previous = spread.choices.map(c => c.cardId);
      const next = shuffle(pools[spread.id]);
      // Guarantee a fresh selection, even with an unlucky/constant random source.
      if (sameMembers(next.slice(0, 3), previous)) [next[0], next[3]] = [next[3], next[0]];
      return { ...state, step: 5, cardId: null,
        deal: { ...state.deal, pools: { ...state.deal.pools, [spread.id]: next } } };
    }
    if (action.type === 'choose' && state.step === 5 && !getSpread(state).choices.some(c => c.cardId === action.id)) return state;
    return { ...base.transition({ ...state, schema: 1 }, action), schema: 2, deal: state.deal };
  }
  function fromOutcome(id) {
    const state = base.fromOutcome(id);
    if (!state || state.contentVersion !== version) return null;
    const drawn = { ...state, schema: 2, deal: deal() };
    const pool = drawn.deal.pools[library.getSpread(state.answers, version).id];
    const at = pool.indexOf(state.cardId);
    if (at >= 3) [pool[0], pool[at]] = [pool[at], pool[0]];
    return drawn;
  }
  return { initial, restore, transition, fromOutcome, getQuestions, getSpread };
}

/** Apply the approved composition without changing answers, cards or outcome copy. */
function applyEditorialPresentation(content, state, { composition = 'standard', outcomeId } = {}) {
  if (state.step === 1) content.classList.add('question-offset');
  if (state.step === 5) content.setAttribute('data-draw-layout', state.deal.layout);
  if (state.step !== 7) return;
  content.classList.add('reading-editorial');
  const title = content.querySelector('.reading-title');
  const cardLabel = title.querySelector('.eyebrow');
  content.querySelector('.eyebrow').append(' / ', cardLabel.textContent);
  cardLabel.remove();
  const copy = content.querySelector('.reading-copy');
  const art = content.querySelector('.reading-art');
  const invitation = copy.querySelector('.invitation');
  content.insertBefore(title, art);
  content.insertBefore(copy, art);
  art.after(invitation);
  const selectedComposition = readingComposition(outcomeId, composition);
  if (!selectedComposition) return;
  content.setAttribute('data-reading-composition', selectedComposition);
  const body = copy.querySelector('p');
  const paragraphs = readingParagraphs(body.textContent);
  if (paragraphs.length === 2) {
    body.textContent = paragraphs[0];
    const reflection = body.cloneNode(false);
    reflection.textContent = paragraphs[1];
    body.after(reflection);
  }
  const arrangement = copy.cloneNode(false);
  arrangement.setAttribute('class', 'reading-composition');
  const eyebrow = content.querySelector('.eyebrow');
  content.insertBefore(arrangement, eyebrow);
  arrangement.append(eyebrow, title, art, copy, invitation);
}

function readingComposition(outcomeId, mode = 'standard') {
  if (['quiet-offset', 'open-page'].includes(mode)) return mode;
  if (mode !== 'mixed' || typeof outcomeId !== 'string' || !outcomeId) return null;
  let hash = 2166136261;
  for (const character of outcomeId) hash = Math.imul(hash ^ character.charCodeAt(0), 16777619);
  return (hash >>> 16) & 1 ? 'open-page' : 'quiet-offset';
}

function readingParagraphs(text) {
  const boundary = [...text.matchAll(/[.!?]+["”’']*(?:\s+|$)/g)][1];
  if (!boundary) return [text];
  const end = boundary.index + boundary[0].length;
  const reflection = text.slice(end).trim();
  return reflection ? [text.slice(0, end).trim(), reflection] : [text];
}

function readingEverydayCopy(text, { discoveryCopy = false } = {}) {
  const original = 'You left room for something unfamiliar. As you look below, notice one shape you would be curious to try. You can take your time with it.';
  return discoveryCopy && text === original
    ? 'You left room for something unfamiliar. As you look below, notice one shape you would be curious to try.'
    : text;
}

function createSignupPopupGuard({ window, document, enabled = false, allowOpening = true, signal, onHide = () => {} }) {
  if (!enabled || signal?.aborted) return () => {};
  let suppressed = false;
  let observer;
  const hidden = new Map();
  function restorePopups() {
    for (const [popup, previous] of hidden) {
      if (previous.display) popup.style.setProperty('display', previous.display, previous.priority);
      else popup.style.removeProperty('display');
      popup.inert = previous.inert;
      for (const [style, media] of previous.styles) style.media = media;
    }
    hidden.clear();
  }
  function suppressPopups() {
    if (!suppressed || signal?.aborted) return;
    const popups = Array.from(document.body.children).filter(node => node.id === 'ic-welcome-popup'
      || (node.tagName === 'DIV' && node.id.startsWith('ic-signup-form-')));
    if (!popups.length) return;
    const hadFocus = popups.some(node => node.contains(document.activeElement));
    for (const popup of popups) {
      if (hidden.has(popup)) continue;
      const styles = popup.id === 'ic-welcome-popup' ? Array.from(popup.querySelectorAll('style'))
        .filter(style => /^\s*body\s*\{\s*overflow:\s*hidden;?\s*\}\s*$/.test(style.textContent)) : [];
      hidden.set(popup, { display: popup.style.getPropertyValue('display'), priority: popup.style.getPropertyPriority('display'),
        inert: popup.inert, styles: styles.map(style => [style, style.media]) });
      popup.style.setProperty('display', 'none', 'important');
      popup.inert = true;
      for (const style of styles) style.media = 'not all';
    }
    if (hadFocus) onHide();
  }
  signal?.addEventListener('abort', () => {
    suppressed = false;
    observer?.disconnect();
    restorePopups();
  }, { once: true });
  return openingScreen => {
    if (signal?.aborted) return;
    suppressed = !openingScreen || !allowOpening;
    observer?.disconnect();
    if (!suppressed) { restorePopups(); return; }
    if (window.MutationObserver) {
      observer ??= new window.MutationObserver(suppressPopups);
      observer.observe(document.body, { childList: true });
    }
    suppressPopups();
  };
}

function readingDealKey(deal) {
  const text = JSON.stringify(deal);
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) hash = Math.imul(hash ^ text.charCodeAt(i), 16777619);
  hash = Math.imul(hash ^ (hash >>> 16), 2246822507);
  hash = Math.imul(hash ^ (hash >>> 13), 3266489909);
  return (hash ^ (hash >>> 16)) >>> 0;
}

function readingProducts(products, { includeClothing = false } = {}) {
  return products.filter(p => p && (includeClothing || !['linen', 'loungewear'].includes(p.category)));
}

function featuredProducts(products, { rotate = false, deal, includeClothing = false } = {}) {
  const catalogue = readingProducts(products, { includeClothing });
  const groups = includeClothing ? [['underwear', 'bras'], ['linen'], ['loungewear']] : [['underwear'], ['bras'], ['layers']];
  return groups.flatMap(categories => {
    const category = categories.join('/');
    const candidates = catalogue.filter(p => categories.includes(p.category));
    const available = candidates.filter(p => p.available !== false);
    if (!rotate || !deal) return (includeClothing ? available : candidates).slice(0, 1);
    if (!available.length) return [];
    return [available.reduce((best, p) => {
      const score = item => readingDealKey([deal, category, item.handle]);
      return score(p) > score(best) ? p : best;
    })];
  });
}

function createCompletionTracker({ window, document, storage, enabled, signal, onIdentified = () => {} }) {
  if (!enabled) return { complete() {} };
  let pending = null, timer = null, tries = 0, running = false, identified = false, privacyRequested = false;
  const sent = new Set();
  const prefix = 'hara-reading:completed:v1:';
  const seen = key => {
    if (sent.has(key)) return true;
    try { return storage?.getItem(prefix + key) === '1'; } catch { return false; }
  };
  const allowed = () => {
    const privacy = window.Shopify?.customerPrivacy;
    return privacy?.analyticsProcessingAllowed?.() === true && privacy?.marketingAllowed?.() === true;
  };
  function schedule() {
    if (!signal?.aborted && timer === null && tries < 120 && (!identified || pending)) {
      timer = window.setTimeout(() => { timer = null; void flush(); }, 500);
    }
  }
  async function flush() {
    if (running || signal?.aborted) return;
    running = true;
    tries++;
    try {
      if (!window.Shopify?.customerPrivacy && !privacyRequested && window.Shopify?.loadFeatures) {
        privacyRequested = true;
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => wake());
      }
      const klaviyo = window.klaviyo;
      if (!allowed() || typeof klaviyo?.isIdentified !== 'function') return;
      const known = await klaviyo.isIdentified();
      if (!known || signal?.aborted || !allowed()) return;
      if (!identified) { identified = true; onIdentified(); }
      if (!pending || typeof klaviyo.track !== 'function') return;
      const event = pending;
      if (seen(event.key)) { pending = null; return; }
      const response = await klaviyo.track('Completed a Reading', event.properties);
      if (response === false) return;
      sent.add(event.key);
      try { storage?.setItem(prefix + event.key, '1'); } catch { /* In-memory deduplication remains. */ }
      if (pending === event) pending = null;
    } catch { /* Analytics must never interrupt a reading. */ }
    finally { running = false; schedule(); }
  }
  function wake() { tries = 0; void flush(); }
  document.addEventListener('visitorConsentCollected', wake, { signal });
  window.addEventListener('load', wake, { signal });
  window.addEventListener('focus', wake, { signal });
  signal?.addEventListener('abort', () => { if (timer !== null) window.clearTimeout(timer); }, { once: true });
  schedule();
  return {
    complete(key, properties) {
      if (signal?.aborted || seen(key)) return;
      pending = { key, properties };
      wake();
    }
  };
}

const READING_BUNDLE = {
  "schemaVersion": 1,
  "contentVersion": "1.0.0",
  "experienceId": "hara-g",
  "locale": "en-AU",
  "questions": [
    {
      "id": "pace",
      "prompt": "What would feel good today?",
      "answers": [
        {
          "id": "slow",
          "label": "A slower pace",
          "assetId": "rhythm"
        },
        {
          "id": "momentum",
          "label": "A little momentum",
          "assetId": "ember"
        }
      ]
    },
    {
      "id": "grounding",
      "prompt": "What would help you find it?",
      "answers": [
        {
          "id": "held",
          "label": "Something to lean on",
          "assetId": "hold"
        },
        {
          "id": "space",
          "label": "Room to find my own way",
          "assetId": "release"
        }
      ]
    },
    {
      "id": "company",
      "prompt": "Where would you like to spend a little time?",
      "answers": [
        {
          "id": "solo",
          "label": "In my own company",
          "assetId": "steady"
        },
        {
          "id": "company",
          "label": "With someone I feel at home with",
          "assetId": "gathering"
        }
      ]
    },
    {
      "id": "wardrobe",
      "prompt": "When you get dressed, which feels closer?",
      "answers": [
        {
          "id": "familiar",
          "label": "The familiar piece I reach for",
          "assetId": "together"
        },
        {
          "id": "new",
          "label": "Something I have yet to try",
          "assetId": "palette"
        }
      ]
    }
  ],
  "assets": {
    "bloom": {
      "src": "assets/1.0.0/bloom.png",
      "alt": "A red flower surrounded by green leaves.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "ember": {
      "src": "assets/1.0.0/ember.png",
      "alt": "A small flame in an earthen bowl beside an olive branch.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "gathering": {
      "src": "assets/1.0.0/gathering.png",
      "alt": "Three ceramic cups arranged together.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "harvest": {
      "src": "assets/1.0.0/harvest.png",
      "alt": "Whole and cut figs beneath a leafy branch.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "hold": {
      "src": "assets/1.0.0/hold.png",
      "alt": "A broad tree with spreading branches.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "horizon": {
      "src": "assets/1.0.0/horizon.png",
      "alt": "A coastal headland beside open water and sky.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "moth": {
      "src": "assets/1.0.0/moth.png",
      "alt": "A moth beside a small patch of light.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "palette": {
      "src": "assets/1.0.0/palette.png",
      "alt": "Pieces of coloured paper arranged in a collage.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "release": {
      "src": "assets/1.0.0/release.png",
      "alt": "A length of pale cloth moving in the air.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "rhythm": {
      "src": "assets/1.0.0/rhythm.png",
      "alt": "Moon phases arranged in a circle above grasses.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "roots": {
      "src": "assets/1.0.0/roots.png",
      "alt": "Leaves above the soil and branching roots below.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "seed": {
      "src": "assets/1.0.0/seed.png",
      "alt": "A sprouting seed in a shallow bowl.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "steady": {
      "src": "assets/1.0.0/steady.png",
      "alt": "Rounded stones resting against one another.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "tide": {
      "src": "assets/1.0.0/tide.png",
      "alt": "Water within a rocky tidal pool.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "together": {
      "src": "assets/1.0.0/together.png",
      "alt": "Overlapping woven threads and pieces of cloth.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "wanderer": {
      "src": "assets/1.0.0/wanderer.png",
      "alt": "Sunlit steps leading through an archway.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    }
  },
  "cards": {
    "seed": {
      "name": "The Seed",
      "assetId": "seed"
    },
    "harvest": {
      "name": "The Harvest",
      "assetId": "harvest"
    },
    "ember": {
      "name": "The Ember",
      "assetId": "ember"
    },
    "rhythm": {
      "name": "The Pause",
      "assetId": "rhythm"
    },
    "tide": {
      "name": "The Tide",
      "assetId": "tide"
    },
    "horizon": {
      "name": "The Horizon",
      "assetId": "horizon"
    },
    "gathering": {
      "name": "The Gathering",
      "assetId": "gathering"
    },
    "roots": {
      "name": "The Roots",
      "assetId": "roots"
    },
    "moth": {
      "name": "The Moth",
      "assetId": "moth"
    },
    "bloom": {
      "name": "The Bloom",
      "assetId": "bloom"
    },
    "wanderer": {
      "name": "The Threshold",
      "assetId": "wanderer"
    }
  },
  "spreads": [
    {
      "id": "slow-held",
      "when": {
        "pace": "slow",
        "grounding": "held"
      },
      "readingIds": [
        "slow-held-seed",
        "slow-held-harvest",
        "slow-held-ember"
      ]
    },
    {
      "id": "slow-space",
      "when": {
        "pace": "slow",
        "grounding": "space"
      },
      "readingIds": [
        "slow-space-rhythm",
        "slow-space-tide",
        "slow-space-horizon"
      ]
    },
    {
      "id": "momentum-held",
      "when": {
        "pace": "momentum",
        "grounding": "held"
      },
      "readingIds": [
        "momentum-held-gathering",
        "momentum-held-roots",
        "momentum-held-ember"
      ]
    },
    {
      "id": "momentum-space",
      "when": {
        "pace": "momentum",
        "grounding": "space"
      },
      "readingIds": [
        "momentum-space-moth",
        "momentum-space-bloom",
        "momentum-space-wanderer"
      ]
    }
  ],
  "readings": {
    "slow-held-seed": {
      "cardId": "seed",
      "title": "Let one thing take root.",
      "body": "A seed begins with a small patch of ground. You chose a slower pace and something to lean on. Here, the Seed gives that wish a shape: one thing, given enough care and enough time. A beginning can be modest and still be worth tending.",
      "invitations": {
        "solo": "Choose one small task and give it a quiet ten minutes. Leave the rest for later.",
        "company": "Ask someone to sit with you while you begin one small thing. Their company may be enough."
      }
    },
    "slow-held-harvest": {
      "cardId": "harvest",
      "title": "Stay for the sweetness.",
      "body": "A ripe fig holds the work of a whole season. Beside your wish for a slower pace and something dependable, the Harvest turns towards what is already here. There may be something nourishing in the familiar: a meal, a place to sit, a habit that has quietly held you.",
      "invitations": {
        "solo": "Give an ordinary pleasure your full attention today. Eat something slowly, while it is still warm.",
        "company": "Share something simple with someone you trust. Let a meal or a cup of tea be the whole occasion."
      }
    },
    "slow-held-ember": {
      "cardId": "ember",
      "title": "A quiet kind of courage.",
      "body": "A small flame needs shelter before it can grow. You chose a slower pace and something to lean on. In this reading, the Ember is a beginning you can tend gently. Make the first step small enough to take without rushing the rest.",
      "invitations": {
        "solo": "Write down one thing you would like to try. Find the smallest part you could begin today.",
        "company": "Tell someone you trust about a small beginning. Ask for the kind of company that would make it easier."
      }
    },
    "slow-space-rhythm": {
      "cardId": "rhythm",
      "title": "Leave a little time unfilled.",
      "body": "The moon makes room for every phase. You chose a slower pace and room to find your own way. The Pause gives that space a place in the day. An empty interval can stay empty long enough for you to notice what you want from it.",
      "invitations": {
        "solo": "Leave ten minutes without a task. Sit by a window, or take the long way home.",
        "company": "Make room for an unhurried conversation. You do not need to arrive with anything to say."
      }
    },
    "slow-space-tide": {
      "cardId": "tide",
      "title": "Let today have its own shape.",
      "body": "Water finds a different edge as the tide moves. Your choices asked for a slower pace and room to move freely. The Tide leaves yesterday’s answer open to change. You might let the day begin gently, then notice where your energy settles.",
      "invitations": {
        "solo": "Pause before a familiar routine. Keep the part that feels good today and let one part change.",
        "company": "Let someone know what kind of company would feel good today. A walk may suit you better than a plan."
      }
    },
    "slow-space-horizon": {
      "cardId": "horizon",
      "title": "A little further to look.",
      "body": "Beyond the headland, the view opens into sky and water. You chose a slower pace and room to find your own way. The Horizon offers a little distance from the things nearest at hand. There may be something to notice when you stop looking quite so closely.",
      "invitations": {
        "solo": "Find a place with a longer view. Give yourself a few minutes to look without reaching for your phone.",
        "company": "Take a slow walk with someone. Let the view give you both something new to notice."
      }
    },
    "momentum-held-gathering": {
      "cardId": "gathering",
      "title": "A place at the table.",
      "body": "Three cups make room for different voices. You chose a little momentum and something to lean on. The Gathering suggests a beginning made easier by welcome: somewhere you can bring an idea before it is finished, and be met with care.",
      "invitations": {
        "solo": "Think of the people whose company makes room for you. Write down one thing you would like to share when you are ready.",
        "company": "Invite someone into a small plan. A shared meal, a walk or half an hour together is enough to begin."
      }
    },
    "momentum-held-roots": {
      "cardId": "roots",
      "title": "Begin with what holds.",
      "body": "Roots make their way through the soil before a new leaf appears. You chose momentum with something dependable beneath it. This card brings your attention to the support already within reach: a useful habit, a learned skill, a person you can ask.",
      "invitations": {
        "solo": "Choose one thing you already know how to do. Let it be the first step towards something less familiar.",
        "company": "Ask someone about a skill or habit that has helped them. Try one small part together."
      }
    },
    "momentum-held-ember": {
      "cardId": "ember",
      "title": "Give the spark some company.",
      "body": "A flame grows where there is something to feed it and shelter around it. You chose momentum and something to lean on. Here, the Ember asks what would help an idea become an attempt. A little encouragement can be part of the beginning.",
      "invitations": {
        "solo": "Set out what you need for one small attempt. Give it a place and a little time of its own.",
        "company": "Tell someone what you want to try, and ask for one concrete kind of help. Begin while the idea is still warm."
      }
    },
    "momentum-space-moth": {
      "cardId": "moth",
      "title": "Follow the small curiosity.",
      "body": "The moth moves towards a little light. You chose momentum and room to find your own way. This card gives curiosity a direction without asking it for a destination. Something has caught your eye; you can give it a few minutes and see what follows.",
      "invitations": {
        "solo": "Follow one small question today. Read a page, take a different turning or look more closely at something ordinary.",
        "company": "Ask someone what has been catching their attention. Follow one of those small curiosities together."
      }
    },
    "momentum-space-bloom": {
      "cardId": "bloom",
      "title": "Make room for pleasure.",
      "body": "A flower brings colour to the ordinary work of growing. You chose a little momentum and room to move freely. The Bloom gives pleasure a place in that movement. You can try something because you enjoy it, and let that be enough for today.",
      "invitations": {
        "solo": "Put on a song and give it your full attention. Move, make something or simply listen.",
        "company": "Invite someone into something you both enjoy. Let the pleasure of it be the reason."
      }
    },
    "momentum-space-wanderer": {
      "cardId": "wanderer",
      "title": "Take the step you can see.",
      "body": "The doorway shows only the beginning of the path. You chose momentum and room to find your own way. The Threshold leaves the distant part unplanned. A small experiment can tell you something that standing in the doorway cannot.",
      "invitations": {
        "solo": "Try the first ten minutes of something unfamiliar. Notice what makes you want to continue.",
        "company": "Invite someone to try one small new thing with you. Leave enough room for either of you to change the plan."
      }
    }
  },
  "invitationQuestionId": "company",
  "everydayQuestionId": "wardrobe",
  "everyday": {
    "familiar": "You reached for the familiar. As you look below, begin with a shape you already enjoy wearing. Notice which details make it feel like yours.",
    "new": "You left room for something unfamiliar. As you look below, notice one shape you would be curious to try. You can take your time with it."
  },
  "products": [
    {
      "handle": "maya-low-waist-undies-earth",
      "title": "Maya low waist undies",
      "shape": [
        "Low rise",
        "Mid coverage"
      ],
      "category": "underwear"
    },
    {
      "handle": "eva-g-string-earth",
      "title": "Eva G-string",
      "shape": [
        "Low rise",
        "Minimal coverage"
      ],
      "category": "underwear"
    },
    {
      "handle": "leo-high-cut-bra-earth",
      "title": "Leo high cut bra",
      "shape": [
        "Rectangle neckline"
      ],
      "category": "bras"
    },
    {
      "handle": "stella-low-cut-bra-earth",
      "title": "Stella low cut bra",
      "shape": [
        "Triangle cut",
        "Light coverage"
      ],
      "category": "bras"
    },
    {
      "handle": "manu-long-sleeve-top-earth",
      "title": "Manu long sleeve top",
      "shape": [
        "Boat neckline",
        "Long sleeves"
      ],
      "category": "layers"
    },
    {
      "handle": "ami-bandeau-earth",
      "title": "Ami bandeau",
      "shape": [
        "Strapless",
        "A simple layer"
      ],
      "category": "layers"
    }
  ],
  "recovery": {
    "title": "Let’s begin again.",
    "message": "We could not find that reading. You can choose a new set of cards.",
    "actionLabel": "Begin a new reading"
  }
}
;
const DRAWN_BUNDLE = {
  "schemaVersion": 1,
  "contentVersion": "1.1.0",
  "experienceId": "hara-g",
  "locale": "en-AU",
  "questions": [
    {
      "id": "pace",
      "prompt": "What would feel good today?",
      "answers": [
        {
          "id": "slow",
          "label": "A slower pace",
          "assetId": "rhythm",
          "assetIds": [
            "rhythm",
            "slow-alt"
          ]
        },
        {
          "id": "momentum",
          "label": "A little momentum",
          "assetId": "ember",
          "assetIds": [
            "ember",
            "momentum-alt"
          ]
        }
      ]
    },
    {
      "id": "grounding",
      "prompt": "What would help you find it?",
      "answers": [
        {
          "id": "held",
          "label": "Something to lean on",
          "assetId": "hold",
          "assetIds": [
            "hold",
            "held-alt"
          ]
        },
        {
          "id": "space",
          "label": "Room to find my own way",
          "assetId": "release",
          "assetIds": [
            "release",
            "space-alt"
          ]
        }
      ]
    },
    {
      "id": "company",
      "prompt": "Where would you like to spend a little time?",
      "answers": [
        {
          "id": "solo",
          "label": "In my own company",
          "assetId": "steady",
          "assetIds": [
            "steady",
            "solo-alt"
          ]
        },
        {
          "id": "company",
          "label": "With someone I feel at home with",
          "assetId": "gathering",
          "assetIds": [
            "gathering",
            "company-alt"
          ]
        }
      ]
    },
    {
      "id": "wardrobe",
      "prompt": "When you get dressed, which feels closer?",
      "answers": [
        {
          "id": "familiar",
          "label": "The familiar piece I reach for",
          "assetId": "together",
          "assetIds": [
            "together",
            "familiar-alt"
          ]
        },
        {
          "id": "new",
          "label": "Something I have yet to try",
          "assetId": "palette",
          "assetIds": [
            "palette",
            "new-alt"
          ]
        }
      ]
    }
  ],
  "assets": {
    "bloom": {
      "src": "assets/1.1.0/bloom.png",
      "alt": "A red flower surrounded by green leaves.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "ember": {
      "src": "assets/1.1.0/ember.png",
      "alt": "A small flame in an earthen bowl beside an olive branch.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "gathering": {
      "src": "assets/1.1.0/gathering.png",
      "alt": "Three ceramic cups arranged together.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "harvest": {
      "src": "assets/1.1.0/harvest.png",
      "alt": "Whole and cut figs beneath a leafy branch.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "hold": {
      "src": "assets/1.1.0/hold.png",
      "alt": "A broad tree with spreading branches.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "horizon": {
      "src": "assets/1.1.0/horizon.png",
      "alt": "A coastal headland beside open water and sky.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "moth": {
      "src": "assets/1.1.0/moth.png",
      "alt": "A moth beside a small patch of light.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "palette": {
      "src": "assets/1.1.0/palette.png",
      "alt": "Pieces of coloured paper arranged in a collage.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "release": {
      "src": "assets/1.1.0/release.png",
      "alt": "A length of pale cloth moving in the air.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "rhythm": {
      "src": "assets/1.1.0/rhythm.png",
      "alt": "Moon phases arranged in a circle above grasses.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "roots": {
      "src": "assets/1.1.0/roots.png",
      "alt": "Leaves above the soil and branching roots below.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "seed": {
      "src": "assets/1.1.0/seed.png",
      "alt": "A sprouting seed in a shallow bowl.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "steady": {
      "src": "assets/1.1.0/steady.png",
      "alt": "Rounded stones resting against one another.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "tide": {
      "src": "assets/1.1.0/tide.png",
      "alt": "Water within a rocky tidal pool.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "together": {
      "src": "assets/1.1.0/together.png",
      "alt": "Overlapping woven threads and pieces of cloth.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "wanderer": {
      "src": "assets/1.1.0/wanderer.png",
      "alt": "Sunlit steps leading through an archway.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "nest": {
      "src": "assets/1.1.0/nest.png",
      "alt": "A woven nest with three speckled eggs resting between branches.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "loaf": {
      "src": "assets/1.1.0/loaf.png",
      "alt": "A round loaf and a thick slice of bread resting on linen.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "quilt": {
      "src": "assets/1.1.0/quilt.png",
      "alt": "A patchwork quilt folded over a wooden chair.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "cloud": {
      "src": "assets/1.1.0/cloud.png",
      "alt": "A pale cloud above low ochre hills and a cypress tree.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "shell": {
      "src": "assets/1.1.0/shell.png",
      "alt": "A spiral shell on sand beside a narrow edge of seawater.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "window": {
      "src": "assets/1.1.0/window.png",
      "alt": "An open wooden window looking into a leafy garden.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "trellis": {
      "src": "assets/1.1.0/trellis.png",
      "alt": "A climbing vine winding around a wooden trellis.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "bridge": {
      "src": "assets/1.1.0/bridge.png",
      "alt": "A stone footbridge over a narrow creek.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "basket": {
      "src": "assets/1.1.0/basket.png",
      "alt": "A woven basket holding lemons and leafy greens.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "swallow": {
      "src": "assets/1.1.0/swallow.png",
      "alt": "A swallow banking through the sky above open fields.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "stream": {
      "src": "assets/1.1.0/stream.png",
      "alt": "A winding stream between russet rocks and green plants.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "kite": {
      "src": "assets/1.1.0/kite.png",
      "alt": "A red paper kite above rolling hills, with a loose trailing string.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "slow-alt": {
      "src": "assets/1.1.0/slow-alt.png",
      "alt": "A ceramic teapot and cup on a stone ledge.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "momentum-alt": {
      "src": "assets/1.1.0/momentum-alt.png",
      "alt": "A yellow lemon and two leaves on a sloping tabletop.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "held-alt": {
      "src": "assets/1.1.0/held-alt.png",
      "alt": "A low stone wall supporting a planted garden bed.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "space-alt": {
      "src": "assets/1.1.0/space-alt.png",
      "alt": "A winding sandy path through dry grasses beneath open sky.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "solo-alt": {
      "src": "assets/1.1.0/solo-alt.png",
      "alt": "A single wooden stool beside a potted fern.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "company-alt": {
      "src": "assets/1.1.0/company-alt.png",
      "alt": "Two pears leaning together on a clay plate.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "familiar-alt": {
      "src": "assets/1.1.0/familiar-alt.png",
      "alt": "A well-used ceramic bowl holding three plums on a wooden bench.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "new-alt": {
      "src": "assets/1.1.0/new-alt.png",
      "alt": "Scissors and a spool of plum thread on cream fabric.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    }
  },
  "cards": {
    "seed": {
      "name": "The Seed",
      "assetId": "seed"
    },
    "harvest": {
      "name": "The Harvest",
      "assetId": "harvest"
    },
    "ember": {
      "name": "The Ember",
      "assetId": "ember"
    },
    "rhythm": {
      "name": "The Pause",
      "assetId": "rhythm"
    },
    "tide": {
      "name": "The Tide",
      "assetId": "tide"
    },
    "horizon": {
      "name": "The Horizon",
      "assetId": "horizon"
    },
    "gathering": {
      "name": "The Gathering",
      "assetId": "gathering"
    },
    "roots": {
      "name": "The Roots",
      "assetId": "roots"
    },
    "moth": {
      "name": "The Moth",
      "assetId": "moth"
    },
    "bloom": {
      "name": "The Bloom",
      "assetId": "bloom"
    },
    "wanderer": {
      "name": "The Threshold",
      "assetId": "wanderer"
    },
    "nest": {
      "name": "The Nest",
      "assetId": "nest"
    },
    "loaf": {
      "name": "The Loaf",
      "assetId": "loaf"
    },
    "quilt": {
      "name": "The Quilt",
      "assetId": "quilt"
    },
    "cloud": {
      "name": "The Cloud",
      "assetId": "cloud"
    },
    "shell": {
      "name": "The Shell",
      "assetId": "shell"
    },
    "window": {
      "name": "The Window",
      "assetId": "window"
    },
    "trellis": {
      "name": "The Trellis",
      "assetId": "trellis"
    },
    "bridge": {
      "name": "The Bridge",
      "assetId": "bridge"
    },
    "basket": {
      "name": "The Basket",
      "assetId": "basket"
    },
    "swallow": {
      "name": "The Swallow",
      "assetId": "swallow"
    },
    "stream": {
      "name": "The Stream",
      "assetId": "stream"
    },
    "kite": {
      "name": "The Kite",
      "assetId": "kite"
    }
  },
  "spreads": [
    {
      "id": "slow-held",
      "when": {
        "pace": "slow",
        "grounding": "held"
      },
      "readingIds": [
        "slow-held-seed",
        "slow-held-harvest",
        "slow-held-ember",
        "slow-held-nest",
        "slow-held-loaf",
        "slow-held-quilt"
      ]
    },
    {
      "id": "slow-space",
      "when": {
        "pace": "slow",
        "grounding": "space"
      },
      "readingIds": [
        "slow-space-rhythm",
        "slow-space-tide",
        "slow-space-horizon",
        "slow-space-cloud",
        "slow-space-shell",
        "slow-space-window"
      ]
    },
    {
      "id": "momentum-held",
      "when": {
        "pace": "momentum",
        "grounding": "held"
      },
      "readingIds": [
        "momentum-held-gathering",
        "momentum-held-roots",
        "momentum-held-ember",
        "momentum-held-trellis",
        "momentum-held-bridge",
        "momentum-held-basket"
      ]
    },
    {
      "id": "momentum-space",
      "when": {
        "pace": "momentum",
        "grounding": "space"
      },
      "readingIds": [
        "momentum-space-moth",
        "momentum-space-bloom",
        "momentum-space-wanderer",
        "momentum-space-swallow",
        "momentum-space-stream",
        "momentum-space-kite"
      ]
    }
  ],
  "readings": {
    "slow-held-seed": {
      "cardId": "seed",
      "title": "Let one thing take root.",
      "body": "A seed begins with a small patch of ground. You chose a slower pace and something to lean on. Here, the Seed gives that wish a shape: one thing, given enough care and enough time. A beginning can be modest and still be worth tending.",
      "invitations": {
        "solo": "Choose one small task and give it a quiet ten minutes. Leave the rest for later.",
        "company": "Ask someone to sit with you while you begin one small thing. Their company may be enough."
      }
    },
    "slow-held-harvest": {
      "cardId": "harvest",
      "title": "Stay for the sweetness.",
      "body": "A ripe fig holds the work of a whole season. Beside your wish for a slower pace and something dependable, the Harvest turns towards what is already here. There may be something nourishing in the familiar: a meal, a place to sit, a habit that has quietly held you.",
      "invitations": {
        "solo": "Give an ordinary pleasure your full attention today. Eat something slowly, while it is still warm.",
        "company": "Share something simple with someone you trust. Let a meal or a cup of tea be the whole occasion."
      }
    },
    "slow-held-ember": {
      "cardId": "ember",
      "title": "A quiet kind of courage.",
      "body": "A small flame needs shelter before it can grow. You chose a slower pace and something to lean on. In this reading, the Ember is a beginning you can tend gently. Make the first step small enough to take without rushing the rest.",
      "invitations": {
        "solo": "Write down one thing you would like to try. Find the smallest part you could begin today.",
        "company": "Tell someone you trust about a small beginning. Ask for the kind of company that would make it easier."
      }
    },
    "slow-space-rhythm": {
      "cardId": "rhythm",
      "title": "Leave a little time unfilled.",
      "body": "The moon makes room for every phase. You chose a slower pace and room to find your own way. The Pause gives that space a place in the day. An empty interval can stay empty long enough for you to notice what you want from it.",
      "invitations": {
        "solo": "Leave ten minutes without a task. Sit by a window, or take the long way home.",
        "company": "Make room for an unhurried conversation. You do not need to arrive with anything to say."
      }
    },
    "slow-space-tide": {
      "cardId": "tide",
      "title": "Let today have its own shape.",
      "body": "Water finds a different edge as the tide moves. Your choices asked for a slower pace and room to move freely. The Tide leaves yesterday’s answer open to change. You might let the day begin gently, then notice where your energy settles.",
      "invitations": {
        "solo": "Pause before a familiar routine. Keep the part that feels good today and let one part change.",
        "company": "Let someone know what kind of company would feel good today. A walk may suit you better than a plan."
      }
    },
    "slow-space-horizon": {
      "cardId": "horizon",
      "title": "A little further to look.",
      "body": "Beyond the headland, the view opens into sky and water. You chose a slower pace and room to find your own way. The Horizon offers a little distance from the things nearest at hand. There may be something to notice when you stop looking quite so closely.",
      "invitations": {
        "solo": "Find a place with a longer view. Give yourself a few minutes to look without reaching for your phone.",
        "company": "Take a slow walk with someone. Let the view give you both something new to notice."
      }
    },
    "momentum-held-gathering": {
      "cardId": "gathering",
      "title": "A place at the table.",
      "body": "Three cups make room for different voices. You chose a little momentum and something to lean on. The Gathering suggests a beginning made easier by welcome: somewhere you can bring an idea before it is finished, and be met with care.",
      "invitations": {
        "solo": "Think of the people whose company makes room for you. Write down one thing you would like to share when you are ready.",
        "company": "Invite someone into a small plan. A shared meal, a walk or half an hour together is enough to begin."
      }
    },
    "momentum-held-roots": {
      "cardId": "roots",
      "title": "Begin with what holds.",
      "body": "Roots make their way through the soil before a new leaf appears. You chose momentum with something dependable beneath it. This card brings your attention to the support already within reach: a useful habit, a learned skill, a person you can ask.",
      "invitations": {
        "solo": "Choose one thing you already know how to do. Let it be the first step towards something less familiar.",
        "company": "Ask someone about a skill or habit that has helped them. Try one small part together."
      }
    },
    "momentum-held-ember": {
      "cardId": "ember",
      "title": "Give the spark some company.",
      "body": "A flame grows where there is something to feed it and shelter around it. You chose momentum and something to lean on. Here, the Ember asks what would help an idea become an attempt. A little encouragement can be part of the beginning.",
      "invitations": {
        "solo": "Set out what you need for one small attempt. Give it a place and a little time of its own.",
        "company": "Tell someone what you want to try, and ask for one concrete kind of help. Begin while the idea is still warm."
      }
    },
    "momentum-space-moth": {
      "cardId": "moth",
      "title": "Follow the small curiosity.",
      "body": "The moth moves towards a little light. You chose momentum and room to find your own way. This card gives curiosity a direction without asking it for a destination. Something has caught your eye; you can give it a few minutes and see what follows.",
      "invitations": {
        "solo": "Follow one small question today. Read a page, take a different turning or look more closely at something ordinary.",
        "company": "Ask someone what has been catching their attention. Follow one of those small curiosities together."
      }
    },
    "momentum-space-bloom": {
      "cardId": "bloom",
      "title": "Make room for pleasure.",
      "body": "A flower brings colour to the ordinary work of growing. You chose a little momentum and room to move freely. The Bloom gives pleasure a place in that movement. You can try something because you enjoy it, and let that be enough for today.",
      "invitations": {
        "solo": "Put on a song and give it your full attention. Move, make something or simply listen.",
        "company": "Invite someone into something you both enjoy. Let the pleasure of it be the reason."
      }
    },
    "momentum-space-wanderer": {
      "cardId": "wanderer",
      "title": "Take the step you can see.",
      "body": "The doorway shows only the beginning of the path. You chose momentum and room to find your own way. The Threshold leaves the distant part unplanned. A small experiment can tell you something that standing in the doorway cannot.",
      "invitations": {
        "solo": "Try the first ten minutes of something unfamiliar. Notice what makes you want to continue.",
        "company": "Invite someone to try one small new thing with you. Leave enough room for either of you to change the plan."
      }
    },
    "slow-held-nest": {
      "cardId": "nest",
      "title": "Somewhere soft to land.",
      "body": "A nest is made from what can be found nearby: a little grass, a twig, a place between branches. You asked for a slower pace and something to lean on. The Nest brings that wish close to home. There may be one small comfort you can put within reach before the day asks anything more of you.",
      "invitations": {
        "solo": "Make one place a little more comfortable. Bring over a cushion, open the curtains, or put a glass of water beside your chair.",
        "company": "Ask someone to join you somewhere comfortable. There is no need to make an occasion of it."
      }
    },
    "slow-held-loaf": {
      "cardId": "loaf",
      "title": "Let yourself be nourished.",
      "body": "Bread asks for work, then time to rest. You chose a slower pace and something dependable. The Loaf turns your attention to the ordinary things that keep a day going: food, warmth, a pause long enough to enjoy them. You can make room for one of those things before returning to what needs doing.",
      "invitations": {
        "solo": "Make yourself something simple to eat. Sit down for it, even if only for a few minutes.",
        "company": "Share a snack or a meal with someone. Leave the washing up until you have both finished."
      }
    },
    "slow-held-quilt": {
      "cardId": "quilt",
      "title": "Keep what has softened with use.",
      "body": "A quilt gathers many small pieces into something warm. Beside your wish to slow down and feel supported, it brings the familiar into view. A much-worn jumper, a repeated walk, the chair you always choose. Notice which ordinary things have earned their place in your day.",
      "invitations": {
        "solo": "Take out something you have kept for years. Use it today, or give it a small repair.",
        "company": "Tell someone the story of an ordinary object you have kept. Ask about one of theirs."
      }
    },
    "slow-space-cloud": {
      "cardId": "cloud",
      "title": "Let a thought pass through.",
      "body": "A cloud changes shape while the hills remain beneath it. You chose a slower pace and room to find your own way. This card leaves a little space around whatever is on your mind. You can notice a thought, leave it unfinished, and see what the next few minutes bring.",
      "invitations": {
        "solo": "Look up for a moment. Follow a cloud, a moving branch, or the changing light without reaching for an explanation.",
        "company": "Sit outside with someone for a few minutes. Let there be pauses in the conversation."
      }
    },
    "slow-space-shell": {
      "cardId": "shell",
      "title": "Stay with a small detail.",
      "body": "The curve of a shell is easy to miss on a whole stretch of beach. Your choices asked for a gentler pace and some freedom. The Shell offers something small enough to give your full attention to. A line, a texture, a sound nearby. The rest can wait while you look.",
      "invitations": {
        "solo": "Choose one ordinary thing and look closely. Notice a detail you would usually pass over.",
        "company": "Point out a small detail to someone beside you. Let them show you something they have noticed too."
      }
    },
    "slow-space-window": {
      "cardId": "window",
      "title": "Let the outside in.",
      "body": "An open window changes a room with very little effort. There is air, a sound from the garden, a different patch of light. You chose slowness and room to find your own way. This card offers a small change of surroundings. You can stay where you are and still let something new reach you.",
      "invitations": {
        "solo": "Open a window or step outside for a moment. Notice what you can hear from there.",
        "company": "Find a different place to sit with someone. See what a change of view brings to your conversation."
      }
    },
    "momentum-held-trellis": {
      "cardId": "trellis",
      "title": "Give an idea somewhere to grow.",
      "body": "A climbing plant finds its way along the wood beside it. You chose movement with something to lean on. The Trellis asks what simple support would make a beginning easier: a regular hour, a clear surface, a question answered. Put one of those things in place and see what you can do with it.",
      "invitations": {
        "solo": "Clear a small space for something you want to begin. Leave the first thing you need ready there.",
        "company": "Ask someone to help you make room for a small project. Decide on one thing you can each do."
      }
    },
    "momentum-held-bridge": {
      "cardId": "bridge",
      "title": "Use the crossing within reach.",
      "body": "A bridge offers a way across without asking the stream to stop. Your choices held both a wish to move and a wish for support. This card points towards a practical connection: an introduction, a useful conversation, a piece of advice. There may be a way forward already close enough to try.",
      "invitations": {
        "solo": "Find one useful piece of information for something you want to do. Use it to take a small next step.",
        "company": "Ask someone a question they might be able to help with. Make it specific enough to answer over a cup of tea."
      }
    },
    "momentum-held-basket": {
      "cardId": "basket",
      "title": "Take a few useful things.",
      "body": "A basket carries a handful of things together. You chose momentum and something dependable beside it. The Basket invites a little preparation: choose what will help, gather it within reach, and leave some room. A beginning may need fewer things than you first imagined.",
      "invitations": {
        "solo": "Gather what you need for one small plan. Put back anything that can wait.",
        "company": "Make a short plan with someone. Each bring one thing that will make it easier or more enjoyable."
      }
    },
    "momentum-space-swallow": {
      "cardId": "swallow",
      "title": "Try a different turning.",
      "body": "A swallow changes direction on the wing. You chose movement and room to find your own way. This card makes room for a change of mind while you are already trying. Follow the part that catches your attention, then notice whether you want to stay with it or turn again.",
      "invitations": {
        "solo": "Take a different route through something familiar today. Notice one thing the change lets you see.",
        "company": "Let someone choose the first part of a walk or a small outing. Choose the next part yourself."
      }
    },
    "momentum-space-stream": {
      "cardId": "stream",
      "title": "Find the opening.",
      "body": "A stream turns around the stones in its path. Beside your wish for movement and freedom, it offers a way to begin with what is possible today. There may be a smaller opening than the one you had in mind. Try it, and let the attempt show you where to go next.",
      "invitations": {
        "solo": "Choose a task you have been circling. Try the easiest part for five minutes, then decide whether to continue.",
        "company": "Try a small part of an idea with someone. Let each of you suggest a different way to begin."
      }
    },
    "momentum-space-kite": {
      "cardId": "kite",
      "title": "Give the afternoon a little play.",
      "body": "A kite needs room to move and someone willing to see what the wind will do. You chose momentum and space. This card gives play a place among your plans. Make a small attempt without deciding what it must become. There may be pleasure in finding out as you go.",
      "invitations": {
        "solo": "Spend ten minutes making or trying something for the pleasure of it. Let it stay unfinished if you like.",
        "company": "Invite someone to do something playful: draw, dance, throw a ball, or invent a small game together."
      }
    }
  },
  "invitationQuestionId": "company",
  "everydayQuestionId": "wardrobe",
  "everyday": {
    "familiar": "You reached for the familiar. As you look below, begin with a shape you already enjoy wearing. Notice which details make it feel like yours.",
    "new": "You left room for something unfamiliar. As you look below, notice one shape you would be curious to try. You can take your time with it."
  },
  "products": [
    {
      "handle": "maya-low-waist-undies-earth",
      "title": "Maya low waist undies",
      "shape": [
        "Low rise",
        "Mid coverage"
      ],
      "category": "underwear"
    },
    {
      "handle": "eva-g-string-earth",
      "title": "Eva G-string",
      "shape": [
        "Low rise",
        "Minimal coverage"
      ],
      "category": "underwear"
    },
    {
      "handle": "leo-high-cut-bra-earth",
      "title": "Leo high cut bra",
      "shape": [
        "Rectangle neckline"
      ],
      "category": "bras"
    },
    {
      "handle": "stella-low-cut-bra-earth",
      "title": "Stella low cut bra",
      "shape": [
        "Triangle cut",
        "Light coverage"
      ],
      "category": "bras"
    },
    {
      "handle": "manu-long-sleeve-top-earth",
      "title": "Manu long sleeve top",
      "shape": [
        "Boat neckline",
        "Long sleeves"
      ],
      "category": "layers"
    },
    {
      "handle": "ami-bandeau-earth",
      "title": "Ami bandeau",
      "shape": [
        "Strapless",
        "A simple layer"
      ],
      "category": "layers"
    }
  ],
  "recovery": {
    "title": "Let’s begin again.",
    "message": "We could not find that reading. You can choose a new set of cards.",
    "actionLabel": "Begin a new reading"
  },
  "presentation": {
    "mode": "drawn",
    "drawSize": 3
  }
}
;
const PERSPECTIVE_BUNDLE = {
  "schemaVersion": 1,
  "contentVersion": "1.2.0",
  "experienceId": "hara-g",
  "locale": "en-AU",
  "questions": [
    {
      "id": "pace",
      "prompt": "What would feel good today?",
      "answers": [
        {
          "id": "slow",
          "label": "A slower pace",
          "assetId": "rhythm",
          "assetIds": [
            "rhythm",
            "slow-alt"
          ]
        },
        {
          "id": "momentum",
          "label": "A little momentum",
          "assetId": "ember",
          "assetIds": [
            "ember",
            "momentum-alt"
          ]
        }
      ]
    },
    {
      "id": "grounding",
      "prompt": "What would help you find it?",
      "answers": [
        {
          "id": "held",
          "label": "Something to lean on",
          "assetId": "hold",
          "assetIds": [
            "hold",
            "held-alt"
          ]
        },
        {
          "id": "space",
          "label": "Room to find my own way",
          "assetId": "release",
          "assetIds": [
            "release",
            "space-alt"
          ]
        }
      ]
    },
    {
      "id": "company",
      "prompt": "Where would you like to spend a little time?",
      "answers": [
        {
          "id": "solo",
          "label": "In my own company",
          "assetId": "steady",
          "assetIds": [
            "steady",
            "solo-alt"
          ]
        },
        {
          "id": "company",
          "label": "With someone I feel at home with",
          "assetId": "gathering",
          "assetIds": [
            "gathering",
            "company-alt"
          ]
        }
      ]
    },
    {
      "id": "wardrobe",
      "prompt": "When you get dressed, which feels closer?",
      "answers": [
        {
          "id": "familiar",
          "label": "The familiar piece I reach for",
          "assetId": "together",
          "assetIds": [
            "together",
            "familiar-alt"
          ]
        },
        {
          "id": "new",
          "label": "Something I have yet to try",
          "assetId": "palette",
          "assetIds": [
            "palette",
            "new-alt"
          ]
        }
      ]
    }
  ],
  "assets": {
    "bloom": {
      "src": "assets/1.2.0/bloom.png",
      "alt": "A red flower surrounded by green leaves.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "ember": {
      "src": "assets/1.2.0/ember.png",
      "alt": "A small flame in an earthen bowl beside an olive branch.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "gathering": {
      "src": "assets/1.2.0/gathering.png",
      "alt": "Three ceramic cups arranged together.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "harvest": {
      "src": "assets/1.2.0/harvest.png",
      "alt": "Whole and cut figs beneath a leafy branch.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "hold": {
      "src": "assets/1.2.0/hold.png",
      "alt": "A broad tree with spreading branches.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "horizon": {
      "src": "assets/1.2.0/horizon.png",
      "alt": "A coastal headland beside open water and sky.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "moth": {
      "src": "assets/1.2.0/moth.png",
      "alt": "A moth beside a small patch of light.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "palette": {
      "src": "assets/1.2.0/palette.png",
      "alt": "Pieces of coloured paper arranged in a collage.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "release": {
      "src": "assets/1.2.0/release.png",
      "alt": "A length of pale cloth moving in the air.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "rhythm": {
      "src": "assets/1.2.0/rhythm.png",
      "alt": "Moon phases arranged in a circle above grasses.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "roots": {
      "src": "assets/1.2.0/roots.png",
      "alt": "Leaves above the soil and branching roots below.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "seed": {
      "src": "assets/1.2.0/seed.png",
      "alt": "A sprouting seed in a shallow bowl.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "steady": {
      "src": "assets/1.2.0/steady.png",
      "alt": "Rounded stones resting against one another.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "tide": {
      "src": "assets/1.2.0/tide.png",
      "alt": "Water within a rocky tidal pool.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "together": {
      "src": "assets/1.2.0/together.png",
      "alt": "Overlapping woven threads and pieces of cloth.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "wanderer": {
      "src": "assets/1.2.0/wanderer.png",
      "alt": "Sunlit steps leading through an archway.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "nest": {
      "src": "assets/1.2.0/nest.png",
      "alt": "A woven nest with three speckled eggs resting between branches.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "loaf": {
      "src": "assets/1.2.0/loaf.png",
      "alt": "A round loaf and a thick slice of bread resting on linen.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "quilt": {
      "src": "assets/1.2.0/quilt.png",
      "alt": "A patchwork quilt folded over a wooden chair.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "cloud": {
      "src": "assets/1.2.0/cloud.png",
      "alt": "A pale cloud above low ochre hills and a cypress tree.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "shell": {
      "src": "assets/1.2.0/shell.png",
      "alt": "A spiral shell on sand beside a narrow edge of seawater.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "window": {
      "src": "assets/1.2.0/window.png",
      "alt": "An open wooden window looking into a leafy garden.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "trellis": {
      "src": "assets/1.2.0/trellis.png",
      "alt": "A climbing vine winding around a wooden trellis.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "bridge": {
      "src": "assets/1.2.0/bridge.png",
      "alt": "A stone footbridge over a narrow creek.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "basket": {
      "src": "assets/1.2.0/basket.png",
      "alt": "A woven basket holding lemons and leafy greens.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "swallow": {
      "src": "assets/1.2.0/swallow.png",
      "alt": "A swallow banking through the sky above open fields.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "stream": {
      "src": "assets/1.2.0/stream.png",
      "alt": "A winding stream between russet rocks and green plants.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "kite": {
      "src": "assets/1.2.0/kite.png",
      "alt": "A red paper kite above rolling hills, with a loose trailing string.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "slow-alt": {
      "src": "assets/1.2.0/slow-alt.png",
      "alt": "A ceramic teapot and cup on a stone ledge.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "momentum-alt": {
      "src": "assets/1.2.0/momentum-alt.png",
      "alt": "A yellow lemon and two leaves on a sloping tabletop.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "held-alt": {
      "src": "assets/1.2.0/held-alt.png",
      "alt": "A low stone wall supporting a planted garden bed.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "space-alt": {
      "src": "assets/1.2.0/space-alt.png",
      "alt": "A winding sandy path through dry grasses beneath open sky.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "solo-alt": {
      "src": "assets/1.2.0/solo-alt.png",
      "alt": "A single wooden stool beside a potted fern.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "company-alt": {
      "src": "assets/1.2.0/company-alt.png",
      "alt": "Two pears leaning together on a clay plate.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "familiar-alt": {
      "src": "assets/1.2.0/familiar-alt.png",
      "alt": "A well-used ceramic bowl holding three plums on a wooden bench.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "new-alt": {
      "src": "assets/1.2.0/new-alt.png",
      "alt": "Scissors and a spool of plum thread on cream fabric.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "endless-ribbon": {
      "src": "assets/1.2.0/endless-ribbon.png",
      "alt": "An ochre paper ribbon turns in a continuous loop, with a vine following its surface.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "leaf-and-wing": {
      "src": "assets/1.2.0/leaf-and-wing.png",
      "alt": "Olive leaves become ochre birds lifting into a pale sky.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    },
    "other-ways-up": {
      "src": "assets/1.2.0/other-ways-up.png",
      "alt": "Three women and olive trees inhabit stairways with different directions of gravity.",
      "width": 1024,
      "height": 1536,
      "mimeType": "image/png"
    }
  },
  "cards": {
    "seed": {
      "name": "The Seed",
      "assetId": "seed"
    },
    "harvest": {
      "name": "The Harvest",
      "assetId": "harvest"
    },
    "ember": {
      "name": "The Ember",
      "assetId": "ember"
    },
    "rhythm": {
      "name": "The Pause",
      "assetId": "rhythm"
    },
    "tide": {
      "name": "The Tide",
      "assetId": "tide"
    },
    "horizon": {
      "name": "The Horizon",
      "assetId": "horizon"
    },
    "gathering": {
      "name": "The Gathering",
      "assetId": "gathering"
    },
    "roots": {
      "name": "The Roots",
      "assetId": "roots"
    },
    "moth": {
      "name": "The Moth",
      "assetId": "moth"
    },
    "bloom": {
      "name": "The Bloom",
      "assetId": "bloom"
    },
    "wanderer": {
      "name": "The Threshold",
      "assetId": "wanderer"
    },
    "nest": {
      "name": "The Nest",
      "assetId": "nest"
    },
    "loaf": {
      "name": "The Loaf",
      "assetId": "loaf"
    },
    "quilt": {
      "name": "The Quilt",
      "assetId": "quilt"
    },
    "cloud": {
      "name": "The Cloud",
      "assetId": "cloud"
    },
    "shell": {
      "name": "The Shell",
      "assetId": "shell"
    },
    "window": {
      "name": "The Window",
      "assetId": "window"
    },
    "trellis": {
      "name": "The Trellis",
      "assetId": "trellis"
    },
    "bridge": {
      "name": "The Bridge",
      "assetId": "bridge"
    },
    "basket": {
      "name": "The Basket",
      "assetId": "basket"
    },
    "swallow": {
      "name": "The Swallow",
      "assetId": "swallow"
    },
    "stream": {
      "name": "The Stream",
      "assetId": "stream"
    },
    "kite": {
      "name": "The Kite",
      "assetId": "kite"
    },
    "endless-ribbon": {
      "name": "The Ribbon",
      "assetId": "endless-ribbon"
    },
    "leaf-and-wing": {
      "name": "Leaf & Wing",
      "assetId": "leaf-and-wing"
    },
    "other-ways-up": {
      "name": "Another Way Up",
      "assetId": "other-ways-up"
    }
  },
  "spreads": [
    {
      "id": "slow-held",
      "when": {
        "pace": "slow",
        "grounding": "held"
      },
      "readingIds": [
        "slow-held-seed",
        "slow-held-harvest",
        "slow-held-ember",
        "slow-held-nest",
        "slow-held-loaf",
        "slow-held-quilt",
        "slow-held-endless-ribbon"
      ]
    },
    {
      "id": "slow-space",
      "when": {
        "pace": "slow",
        "grounding": "space"
      },
      "readingIds": [
        "slow-space-rhythm",
        "slow-space-tide",
        "slow-space-horizon",
        "slow-space-cloud",
        "slow-space-shell",
        "slow-space-window",
        "slow-space-other-ways-up"
      ]
    },
    {
      "id": "momentum-held",
      "when": {
        "pace": "momentum",
        "grounding": "held"
      },
      "readingIds": [
        "momentum-held-gathering",
        "momentum-held-roots",
        "momentum-held-ember",
        "momentum-held-trellis",
        "momentum-held-bridge",
        "momentum-held-basket",
        "momentum-held-endless-ribbon"
      ]
    },
    {
      "id": "momentum-space",
      "when": {
        "pace": "momentum",
        "grounding": "space"
      },
      "readingIds": [
        "momentum-space-moth",
        "momentum-space-bloom",
        "momentum-space-wanderer",
        "momentum-space-swallow",
        "momentum-space-stream",
        "momentum-space-kite",
        "momentum-space-leaf-and-wing"
      ]
    }
  ],
  "readings": {
    "slow-held-seed": {
      "cardId": "seed",
      "title": "Let one thing take root.",
      "body": "For a while, a planted seed leaves the surface of the soil unchanged. You water the same patch of earth and wait. There is a kind of care in returning before there is anything to show for it. If something in your life is still at this stage, you might give it a little shelter from the need to hurry. Let it have time beneath the surface.",
      "invitations": {
        "solo": "Is there something you would like to keep tending? You might leave a little room for it tomorrow, too.",
        "company": "You might tell someone about a beginning that is still taking shape. Let them know whether you would welcome help or simply a listening ear."
      }
    },
    "slow-held-harvest": {
      "cardId": "harvest",
      "title": "Stay for the sweetness.",
      "body": "A fig grows heavy on its branch. Inside, the fruit has softened and sweetened through days you may scarcely have noticed. There is a moment for picking, and a moment for sitting down to eat. Perhaps something you have been tending is ready to be enjoyed. Stay with it a while before turning to what needs doing next.",
      "invitations": {
        "solo": "Something you enjoy eating, a comfortable place to sit, enough time to taste it. That could be your pause today.",
        "company": "If you feel like sharing, put something good on a plate for two. The company can be the occasion."
      }
    },
    "slow-held-ember": {
      "cardId": "ember",
      "title": "Keep a little warmth.",
      "body": "There is still warmth among the ashes. Close to the ember, a hand can feel what is almost invisible from across the room. Some wishes stay with us like this, asking very little for a while. You can sit near yours without deciding what it must become. There will be time to see whether you want to give it more air.",
      "invitations": {
        "solo": "You might return to an idea you put aside. Read a few lines, open the notebook, or simply remember what drew you to it.",
        "company": "With someone you trust, there may be room to speak about a wish you have kept to yourself. It can stay unfinished."
      }
    },
    "slow-space-rhythm": {
      "cardId": "rhythm",
      "title": "Leave a little time unfilled.",
      "body": "The moon is there on evenings when you forget to look. When you notice it again, its shape has changed. A pause can bring you back to things that have continued quietly beyond your attention. You might leave a little time without deciding what belongs in it. Sit long enough for the room, the sky, or your own thoughts to become more noticeable.",
      "invitations": {
        "solo": "If a gap opens in the day, you could leave it open. There is no need to choose an activity for it.",
        "company": "You might spend some unhurried time beside someone. Conversation can come and go."
      }
    },
    "slow-space-tide": {
      "cardId": "tide",
      "title": "Let today have its own shape.",
      "body": "At the water’s edge, yesterday’s footprints have gone. The tide has reached further up the sand, then drawn away, leaving a different shore to walk. What suited you yesterday may feel different this morning. There is room to notice that before you settle into the day. Keep close to what feels welcome, and allow yourself to choose again where you need to.",
      "invitations": {
        "solo": "Before following your usual routine, you might pause to see which part you want today. One part can change.",
        "company": "Let someone know what sort of company you would enjoy today. A quiet visit may feel different from going out."
      }
    },
    "slow-space-horizon": {
      "cardId": "horizon",
      "title": "A little further to look.",
      "body": "From the headland, the nearest rocks give way to open water. Your eye can travel further than your feet need to. With a little distance, what filled the foreground begins to sit among other things. You can take in the wider view without finding an answer there. For a while, there is sky and water, and room to look beyond the matter closest at hand.",
      "invitations": {
        "solo": "A view down a street or through a window may be enough. Let your eyes rest on something further away.",
        "company": "You could find a place with a view together. There may be pleasure in looking at the same thing for a while."
      }
    },
    "momentum-held-gathering": {
      "cardId": "gathering",
      "title": "A place at the table.",
      "body": "There are cups on the table and room for another chair. Someone begins a story; someone else remembers something they have been meaning to ask. An idea can find its first words in company like this. You may have something you would like to bring to the table before you know quite what it is. Leave room for another voice to help it take shape.",
      "invitations": {
        "solo": "You might jot down something you would like to talk about when the right company comes along. It can wait until you are ready.",
        "company": "Is there someone with whom you can think aloud? Invite them for a walk or a cup of tea."
      }
    },
    "momentum-held-roots": {
      "cardId": "roots",
      "title": "Begin with what holds.",
      "body": "Below the new leaves, roots branch through ground the plant already occupies. Much of what supports this season’s growth has been there for some time. You, too, bring things with you: what practice has taught your hands, a lesson you remember, knowledge passed on by someone patient. As you begin, there may be more to draw on than you first thought.",
      "invitations": {
        "solo": "Think of something you once had to learn slowly. Is there a part of that knowledge you could use now?",
        "company": "You might ask someone to show you a skill they know well. Give yourselves time to try it side by side."
      }
    },
    "momentum-held-ember": {
      "cardId": "ember",
      "title": "Give the spark some company.",
      "body": "An ember catches a dry twig. A little air reaches it, and the flame begins to take. The wish to try something can be present long before the conditions are right. Perhaps one of those conditions is within reach now: a place to work, someone’s encouragement, an hour you can share. Bring it close to the idea and see what begins.",
      "invitations": {
        "solo": "What would make an idea easier to try? You could set out what you need and see whether you feel like beginning.",
        "company": "Tell someone what you would like to try and what help would be welcome. They may be glad to have something specific to offer."
      }
    },
    "momentum-space-moth": {
      "cardId": "moth",
      "title": "Follow the small curiosity.",
      "body": "In the dimness, a patch of light draws the moth into view. Your attention may already be resting somewhere like this: on a question, a colour, a line you want to read again. You can follow that interest for a while without knowing where it leads. Look a little closer. There may be enough pleasure in the looking to keep you there.",
      "invitations": {
        "solo": "What has caught your attention lately? You might read a little more, look again, or try making something with it.",
        "company": "Ask someone what they have been curious about lately. Perhaps there is something you would both enjoy finding out."
      }
    },
    "momentum-space-bloom": {
      "cardId": "bloom",
      "title": "Make room for pleasure.",
      "body": "A flower opens, and a familiar corner of the garden suddenly holds your attention. There is colour where yesterday you passed without looking. Pleasure can change the way you inhabit an ordinary day. You might follow it into a song, a conversation, something you make with your hands. Give a little time to what you enjoy, and see whether you want to stay longer.",
      "invitations": {
        "solo": "Choose something simply because you would enjoy it. A song is long enough to begin.",
        "company": "You could invite someone to share a pleasure you have in common. There need not be another reason."
      }
    },
    "momentum-space-wanderer": {
      "cardId": "wanderer",
      "title": "Take the step you can see.",
      "body": "Through the doorway, only the first part of the path is visible. From here, you can see enough to step outside, though the view beyond the turn remains hidden. Trying something may bring the next part into sight. You can go a little way, pause, and decide from there. The doorway will still be behind you if you want to return.",
      "invitations": {
        "solo": "You might try the opening part of something unfamiliar. Let yourself decide whether to continue after you have begun.",
        "company": "Invite someone to try something new with you. Agree that either of you can suggest a change along the way."
      }
    },
    "slow-held-nest": {
      "cardId": "nest",
      "title": "Somewhere soft to land.",
      "body": "A twig catches between branches. Grass fills a gap, and the hollow begins to hold its shape. A nest is built from many such adjustments, each making a place more habitable. Nearer to home, you may know the chair that catches the afternoon light or the cup that fits your hands. Let one of those familiar comforts welcome you back into the day.",
      "invitations": {
        "solo": "Where would you like to sit for a while? Bring whatever would make that place more comfortable.",
        "company": "You could ask someone to come and sit with you. There is no need to tidy the whole house first."
      }
    },
    "slow-held-loaf": {
      "cardId": "loaf",
      "title": "Let yourself be nourished.",
      "body": "Hands work the dough, then cover it and leave it to rise. Later, someone breaks the bread and passes it across the table. Making and receiving both have their place here. If you have been busy with the making, there may be a moment now to sit down and be fed. Warmth, a full plate, a pause in the work. Ordinary things that matter.",
      "invitations": {
        "solo": "If you can, sit down with something you enjoy eating. Let the meal have your attention for a while.",
        "company": "A shared meal could be as simple as toast. You might let someone else make it, or make enough for them."
      }
    },
    "slow-held-quilt": {
      "cardId": "quilt",
      "title": "Keep what has softened with use.",
      "body": "Look closely at a quilt and the joins begin to show. Different pieces, held together by stitches someone took time to make. Use leaves its own marks: a faded corner, a softened fold, a repair. There may be something in your day whose familiarity comes from years of such attention. You can let it be enough company for a quiet moment.",
      "invitations": {
        "solo": "Perhaps there is an old cup, book or keepsake you would like to use again. Take it out and spend a little time with it.",
        "company": "Ask someone about an ordinary thing they have kept for years. You might have a story of your own to share."
      }
    },
    "slow-space-cloud": {
      "cardId": "cloud",
      "title": "Let a thought pass through.",
      "body": "A cloud draws a shadow across the hills. As you watch, the outline loosens and the light changes again. It is possible to stay with something without holding it still. A thought may be like that today, clear for a moment, then less certain. You can let it move through the afternoon without deciding what to make of it.",
      "invitations": {
        "solo": "For a moment, you might watch something that changes: a shadow, a branch in the wind, the light on a wall.",
        "company": "Find a little time to sit outside together, if you can. Leave room for quiet between the words."
      }
    },
    "slow-space-shell": {
      "cardId": "shell",
      "title": "Stay with a small detail.",
      "body": "Among all the things along a shore, one curve catches your eye. A shell has fine lines you can follow, colour gathered at an edge, places worn smooth. The longer you look, the more there is to see. Perhaps this is enough to be interested in for now. Something close at hand, asking only that you notice it.",
      "invitations": {
        "solo": "Choose something nearby that you usually glance past. You might find a detail you have never noticed.",
        "company": "Show someone a detail that caught your attention. See what catches theirs."
      }
    },
    "slow-space-window": {
      "cardId": "window",
      "title": "Let the outside in.",
      "body": "An open window brings the garden within earshot. A bird calls, air moves through the room, and the curtain lifts from its place. You are still at home, but the room feels different. There may be a change available to you that asks very little. A door left open, a different seat, a few minutes where the light falls.",
      "invitations": {
        "solo": "You could open a window or step outside. Stay for a moment and listen to what reaches you.",
        "company": "Try sitting somewhere different together. A new view may bring something unexpected into the conversation."
      }
    },
    "momentum-held-trellis": {
      "cardId": "trellis",
      "title": "Give an idea somewhere to grow.",
      "body": "The trellis has a simple shape. Along it, the climbing stems find their own direction, reaching through one opening and curling around another. A little structure can leave plenty of room for discovery. Perhaps a regular afternoon or a cleared corner would give something you want to do a place in your life. The details can grow as you go.",
      "invitations": {
        "solo": "You might give an interest a regular place or time. Keep it simple enough that you will want to return.",
        "company": "Would it help to make a regular time with someone? You can each bring something of your own to work on."
      }
    },
    "momentum-held-bridge": {
      "cardId": "bridge",
      "title": "Meet at the crossing.",
      "body": "The stream runs between two banks. Across it, a bridge joins paths that would otherwise remain apart. Sometimes a way forward begins with contact: asking a question, making an introduction, hearing from someone who has been there. You can reach across while keeping your own footing. There may be something useful to learn from the person on the other side.",
      "invitations": {
        "solo": "You could look for an account from someone who has done what interests you. See which part of their experience is useful to yours.",
        "company": "Perhaps there is one question you have wanted to ask someone. Give them a little context, and room to answer in their own way."
      }
    },
    "momentum-held-basket": {
      "cardId": "basket",
      "title": "Take what you need.",
      "body": "A basket has room for a few useful things, and a handle that lets you carry them together. What belongs inside depends on where you are going. Before setting out, you can choose what will be helpful and leave the rest where it is. A little preparation may be enough to make the next part feel possible. There can still be space for something you find along the way.",
      "invitations": {
        "solo": "For something you would like to do, gather just what you need to begin. You can come back for the rest.",
        "company": "If you are making a plan together, decide what each of you can bring. Leave room for something enjoyable, too."
      }
    },
    "momentum-space-swallow": {
      "cardId": "swallow",
      "title": "Try a different turning.",
      "body": "A swallow turns in the open air. For a moment you follow its course, then it changes direction and your eye follows again. There can be room to respond while you are already moving. What you notice along the way may interest you more than the route you first chose. You are allowed to take that interest seriously and see where it leads.",
      "invitations": {
        "solo": "On a familiar errand or walk, you might leave room for a different turning. See what draws your attention.",
        "company": "Set out together without deciding every part of the outing. Take turns suggesting where to go next."
      }
    },
    "momentum-space-stream": {
      "cardId": "stream",
      "title": "Find the opening.",
      "body": "Water gathers at a stone, then slips through a narrow space beside it. A little further on, the stream widens again. If the way you had in mind is awkward today, there may be another part you can begin. Start where there is room. What you learn there may help you return to the difficult part with a different approach.",
      "invitations": {
        "solo": "Is there an easier part of something you want to do? You could begin there and leave the difficult part for another moment.",
        "company": "Look at an idea together and see where each of you would begin. One of you may notice an opening the other has missed."
      }
    },
    "momentum-space-kite": {
      "cardId": "kite",
      "title": "Give the afternoon a little play.",
      "body": "The kite lifts, dips, and catches the wind again. The person holding the string makes a small adjustment, then waits to see what happens. There is enjoyment in this exchange between trying and finding out. You might let part of the day unfold that way. Make an attempt, follow what happens, and leave a little room to be surprised by it.",
      "invitations": {
        "solo": "You could draw, dance, make something, or try an idea for the pleasure of finding out what happens. It can stay unfinished.",
        "company": "Invite someone into a game or something you can make together. Leave the result undecided."
      }
    },
    "slow-held-endless-ribbon": {
      "cardId": "endless-ribbon",
      "title": "Return to something that steadies you.",
      "body": "Follow the ribbon with your eye and it brings you round to where you began. The vine rests against each fold. A familiar ritual can give a day this kind of continuity: water poured into a cup, a walk past the same tree, a few pages before sleep. You return to it carrying a different day. There is comfort in finding it waiting.",
      "invitations": {
        "solo": "Which familiar part of your day would you miss? You might give it a little more time today.",
        "company": "A regular walk or cup of tea can be a place to meet again. Perhaps there is someone you would like to return to it with."
      }
    },
    "momentum-held-endless-ribbon": {
      "cardId": "endless-ribbon",
      "title": "Let the familiar carry you further.",
      "body": "The ribbon turns through a fold and shows another side of the same surface. A vine follows it into the turn. Something familiar may have more uses than you have yet discovered. A recipe becomes a meal for new company; a skill finds a different task. You can begin with what you know and let the next turn show you another way to use it.",
      "invitations": {
        "solo": "Take something you enjoy doing and try it in a different setting. Notice what feels familiar and what asks you to learn.",
        "company": "You might bring something you know to a shared plan, and invite someone else to bring something of theirs. See what you can make together."
      }
    },
    "slow-space-other-ways-up": {
      "cardId": "other-ways-up",
      "title": "There is another way to see it.",
      "body": "Each woman finds her footing in a different part of the courtyard. From where she stands, another path becomes visible. You chose a little more time, a little more room. Perhaps there is room for the question, too, to sit beside you without needing an answer yet. Watch how the light moves across the walls. Listen to someone whose view is different from your own. See what you notice when you return.",
      "invitations": {
        "solo": "Look at something familiar from a different place: the other side of a room, a new seat, the far end of your usual walk. Notice one thing you had missed.",
        "company": "Ask someone how an ordinary part of their day looks from where they stand. Give their answer a little room before offering yours."
      }
    },
    "momentum-space-leaf-and-wing": {
      "cardId": "leaf-and-wing",
      "title": "Let a small change take shape.",
      "body": "Along the branch, a leaf begins to resemble a wing. Further up, birds lift into the open sky. Your eye can follow the change from one shape to the next. A new direction may begin in something already close to you, with one detail altered. Give that change a try. You can discover how much you want to keep as it takes shape.",
      "invitations": {
        "solo": "You might change one detail in something you often make or do. See whether the variation feels worth keeping.",
        "company": "Try a different version of something you enjoy together. Compare what each of you would keep for next time."
      }
    }
  },
  "invitationQuestionId": "company",
  "everydayQuestionId": "wardrobe",
  "everyday": {
    "familiar": "You reached for the familiar. As you look below, begin with a shape you already enjoy wearing. Notice which details make it feel like yours.",
    "new": "You left room for something unfamiliar. As you look below, notice one shape you would be curious to try. You can take your time with it."
  },
  "products": [
    {
      "handle": "maya-low-waist-undies-earth",
      "title": "Maya low waist undies",
      "shape": [
        "Low rise",
        "Mid coverage"
      ],
      "category": "underwear"
    },
    {
      "handle": "eva-g-string-earth",
      "title": "Eva G-string",
      "shape": [
        "Low rise",
        "Minimal coverage"
      ],
      "category": "underwear"
    },
    {
      "handle": "leo-high-cut-bra-earth",
      "title": "Leo high cut bra",
      "shape": [
        "Rectangle neckline"
      ],
      "category": "bras"
    },
    {
      "handle": "stella-low-cut-bra-earth",
      "title": "Stella low cut bra",
      "shape": [
        "Triangle cut",
        "Light coverage"
      ],
      "category": "bras"
    },
    {
      "handle": "manu-long-sleeve-top-earth",
      "title": "Manu long sleeve top",
      "shape": [
        "Boat neckline",
        "Long sleeves"
      ],
      "category": "layers"
    },
    {
      "handle": "ami-bandeau-earth",
      "title": "Ami bandeau",
      "shape": [
        "Strapless",
        "A simple layer"
      ],
      "category": "layers"
    }
  ],
  "recovery": {
    "title": "Let’s begin again.",
    "message": "We could not find that reading. You can choose a new set of cards.",
    "actionLabel": "Begin a new reading"
  },
  "presentation": {
    "mode": "drawn",
    "drawSize": 3,
    "poolSize": 7
  }
}
;
// Shopify adapter. Authored content and resolver remain versioned and unchanged.
function storefrontUrl(base, outcomeId) {
  const url = new URL(base);
  const keep = new URLSearchParams();
  for (const key of ['preview_theme_id', 'view', '_fd', 'pb', 'deck', 'layout']) {
    if (url.searchParams.has(key)) keep.set(key, url.searchParams.get(key));
  }
  if (outcomeId) keep.set('reading', outcomeId);
  url.search = keep.toString(); url.hash = '';
  return url.href;
}
function mountReading(root, signal) {
const config = JSON.parse(root.querySelector('[data-reading-config]').textContent);
const main = root.querySelector('[data-reading-main]');
const status = root.querySelector('[data-reading-status]');
const showChoiceRecap = config.showChoiceRecap !== false;
const discoveryCopy = config.discoveryCopy === true;
const updateSignupPopup = createSignupPopupGuard({ window, document,
  enabled: config.popupIntroOnly === true || config.hideSignupPopup === true, allowOpening: config.hideSignupPopup !== true, signal,
  onHide: () => main.querySelector('h1')?.focus({ preventScroll: true }) });
const bundles = config.drawnEnabled ? [READING_BUNDLE, DRAWN_BUNDLE, PERSPECTIVE_BUNDLE] : [READING_BUNDLE];
const translate = text => config.copy[text] || text;
function el(tag, attrs = {}, ...children) {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (key.startsWith('on')) node.addEventListener(key.slice(2), value);
    else node.setAttribute(key, value);
  }
  const cls = attrs.class || '';
  const role = tag === 'h1' ? 'heading' : cls.includes('eyebrow') ? 'label' : cls.includes('small') ? 'small' : 'body';
  node.classList.add(config.typography[role]);
  children.flat().filter(child => child != null).forEach(child => node.append(typeof child === 'string' ? translate(child) : child));
  return node;
}
const label = text => el('p', { class: 'eyebrow' }, text);
const paragraph = (text, className = '') => el('p', { class: className }, text);
const heading = text => el('h1', { tabindex: '-1' }, text);
const button = (text, action, className = 'text-button') => el('button', { type: 'button', class: className, onclick: action }, text);
const artwork = (image, version, { decorative = false, lazy = false } = {}) => el('img', {
  src: config.art[image.id].small,
  srcset: `${config.art[image.id].small} 480w, ${config.art[image.id].large} 800w`,
  sizes: '(max-width: 650px) 45vw, 300px', width: image.width, height: image.height,
  alt: decorative ? '' : image.alt, decoding: 'async', loading: lazy ? 'lazy' : 'eager'
});

if (!bundles) {
  main.replaceChildren(el('section', { class: 'stack error' }, label('A small reading'), heading('The cards could not open.'),
    paragraph('Please try again in a moment.'), button('Try again →', () => location.reload(), 'primary')));
} else {
  const params = new URL(location.href).searchParams;
  const defaultDeck = config.defaultDeck || 'classic';
  const defaultLayout = config.defaultLayout || 'standard';
  const requestedDeck = params.get('deck') || defaultDeck;
  const requestedLayout = params.get('layout') || defaultLayout;
  let emailAttribution = config.trackCompletion ? params.get('_kx') : null;
  const outcomeVersion = params.get('reading')?.match(/^hara-g@(\d+\.\d+\.\d+)\//)?.[1];
  const selectedVersion = outcomeVersion || (requestedDeck === 'perspective' ? '1.2.0' : requestedDeck === 'drawn' ? '1.1.0' : '1.0.0');
  const drawn = config.drawnEnabled && ['1.1.0', '1.2.0'].includes(selectedVersion);
  const editorial = drawn && config.editorialEnabled && requestedLayout === 'editorial';
  root.toggleAttribute('data-editorial-reading', editorial);
  const library = createReadingLibrary(bundles, { defaultVersion: drawn ? selectedVersion : '1.0.0' });
  const journey = drawn ? createDrawJourney(library, bundles.find(b => b.contentVersion === selectedVersion)) : createJourney(library);
  root.toggleAttribute('data-drawn-reading', drawn);
  const storageKey = drawn ? (selectedVersion === '1.2.0' ? 'hara-reading:journey:perspective:v1' : 'hara-reading:journey:drawn:v1') : 'hara-reading:journey:v1';
  let storage;
  try { storage = window.localStorage; } catch { /* Storage is optional. */ }
  let invalidLink = false;
  function restoreOutcome(id, candidate) {
    if (drawn && candidate?.step === 7) {
      const restored = journey.restore(candidate);
      const result = library.tryResolve(restored);
      if (result.ok && result.result.outcomeId === id) return restored;
    }
    return journey.fromOutcome(id);
  }
  const stored = readStored(storage, journey, storageKey);
  let state = params.has('reading') ? restoreOutcome(params.get('reading'), stored) : stored;
  if (!state) { invalidLink = true; state = journey.initial(); }

  const tracker = createCompletionTracker({ window, document, storage, enabled: config.trackCompletion, signal,
    onIdentified: () => { emailAttribution = null; history.replaceState({ ...history.state, haraJourney: state }, '', urlForState()); } });
  function currentResult() {
    const result = library.resolve(state);
    const products = readingProducts(config.products, { includeClothing: config.includeClothing });
    return { ...result, products, featured: featuredProducts(products, { rotate: config.rotateProducts && drawn, deal: state.deal, includeClothing: config.includeClothing }) };
  }
  function urlForState() {
    const base = new URL(location.href);
    const activeDeck = drawn ? (selectedVersion === '1.2.0' ? 'perspective' : 'drawn') : 'classic';
    if (activeDeck !== defaultDeck) base.searchParams.set('deck', activeDeck); else base.searchParams.delete('deck');
    // Shopify consumes preview_theme_id into a cookie before our deferred script runs.
    // Restore it from the rendered theme so a copied reading opens on the same draft.
    if (config.previewThemeId) base.searchParams.set('preview_theme_id', config.previewThemeId);
    if (requestedLayout !== defaultLayout) base.searchParams.set('layout', requestedLayout); else base.searchParams.delete('layout');
    const url = new URL(storefrontUrl(base.href, state.step === 7 ? currentResult().outcomeId : null));
    if (emailAttribution) url.searchParams.set('_kx', emailAttribution);
    return url.href;
  }
  function remember(replace = false) {
    const saved = saveStored(storage, state, storageKey);
    history[replace ? 'replaceState' : 'pushState']({ ...history.state, haraJourney: state }, '', urlForState());
    return saved;
  }
  function act(action) {
    invalidLink = false;
    const previousStep = state.step;
    state = journey.transition(state, action);
    const saved = remember();
    render(true);
    if (action.type === 'read' && previousStep === 6 && state.step === 7) {
      const result = currentResult();
      tracker.complete(`${state.contentVersion}:${readingDealKey(state.deal || result.outcomeId)}`, {
        content_version: state.contentVersion, outcome_id: result.outcomeId, card_id: result.card.id,
        card_name: result.card.name, reading_title: result.title,
        featured_products: result.featured.map(p => p.handle),
        reading_url: storefrontUrl(urlForState(), result.outcomeId)
      });
    }
    if (!saved) status.textContent = 'You can keep going. This browser could not save your progress.';
  }
  const actionButton = (text, type, className) => button(text, () => act({ type }), className);
  function render(focus = false) {
    status.textContent = '';
    const questions = drawn ? journey.getQuestions(state) : library.getQuestions(state.contentVersion);
    let content;
    let pageTitle = 'A small reading';
    if (invalidLink) {
      content = el('section', { class: 'stack error screen' }, label('A small reading'), heading('Let’s begin again.'),
        paragraph('We could not find that reading. You can choose a new set of cards.'), actionButton('Begin a new reading →', 'restart', 'primary'));
    } else if (state.step === 0) {
      const bundle = bundles.find(b => b.contentVersion === state.contentVersion);
      const introArt = el('div', { class: 'art-row', 'aria-hidden': 'true' }, (drawn ? ['nest', 'cloud', 'kite'] : ['seed', 'tide', 'moth']).map(id => artwork({ id, ...bundle.assets[id] }, state.contentVersion, { decorative: true })));
      pageTitle = discoveryCopy ? 'Your HARA reading' : pageTitle;
      content = el('section', { class: 'stack intro screen' }, label('A small reading'), heading(discoveryCopy ? 'Your HARA reading' : 'Find what feels close today.'),
        introArt,
        paragraph(discoveryCopy ? 'We offer four small choices, followed by a reading drawn from the cards that speak to you.' : 'Four small choices. Then an image chosen by you.'),
        discoveryCopy ? paragraph('Each reading holds something to sit with, and a small invitation for your day.') : null,
        paragraph(discoveryCopy ? 'Along the way, get to know HARA and the pieces we make.' : 'We will draw a reading from the things you choose. Something to sit with, and a small invitation for your day.'),
        actionButton('Begin →', 'begin', 'primary'));
    } else if (state.step <= 4) {
      const q = questions[state.step - 1];
      pageTitle = q.prompt;
      content = el('section', { class: 'stack question screen' }, label(`0${state.step} / 04`), heading(q.prompt),
        paragraph(discoveryCopy ? 'Choose what resonates today.' : 'Choose what feels closer today. Both can belong.', 'lead'),
        el('div', { class: 'choices', role: 'group', 'aria-label': q.prompt }, q.answers.map(answer => el('button', {
          type: 'button', class: 'choice', 'aria-pressed': String(state.answers[q.id] === answer.id),
          onclick: () => act({ type: 'answer', id: answer.id })
        }, artwork(answer.image, state.contentVersion, { decorative: true }), el('span', { class: 'label' }, answer.label)))),
        actionButton('Back →', 'back'), actionButton('Begin again', 'restart'));
    } else if (state.step === 5) {
      pageTitle = 'Let an image catch your eye';
      content = el('section', { class: 'stack draw screen' }, label('One last choice'), heading('Let an image catch your eye.'),
        paragraph('Linger for a moment. Choose the card you would like to spend a little time with.', 'lead'),
        el('div', { class: 'choices', role: 'group', 'aria-label': 'Choose an image' }, (drawn ? journey.getSpread(state) : library.getSpread(state.answers, state.contentVersion)).choices.map(choice =>
          el('button', { type: 'button', class: 'choice', onclick: () => act({ type: 'choose', id: choice.cardId }) }, artwork(choice.image, state.contentVersion)))),
        paragraph('There is no right card to find.', 'small'),
        actionButton('Back to my choices →', 'back'), actionButton('Begin again', 'restart'));
    } else {
      const result = currentResult();
      pageTitle = `${result.card.name}: ${result.title}`;
      if (state.step === 6) {
        content = el('section', { class: 'stack hara-reading-reveal screen' }, label('The card you chose'),
          el('div', { class: 'card-image' }, artwork(result.card.image, state.contentVersion)), heading(result.card.name),
          paragraph(result.title), actionButton('Read my card →', 'read', 'primary'), actionButton('Choose another image →', 'redraw'));
      } else {
        content = el('article', { class: `reading screen${showChoiceRecap ? '' : ' reading-without-recap'}` }, label('Your reading'),
          el('div', { class: 'reading-art' }, artwork(result.card.image, state.contentVersion)),
          el('div', { class: 'reading-title' }, label(result.card.name), heading(result.title)),
          el('div', { class: 'reading-copy' }, paragraph(result.reading),
            el('section', { class: 'invitation', 'aria-label': 'A small invitation' }, label('A small invitation'), paragraph(result.invitation))),
          result.featured.length ? el('aside', { class: 'shop-preview', 'aria-labelledby': `shop-preview-title-${config.id}` },
            el('h2', { id: `shop-preview-title-${config.id}`, class: 'eyebrow' }, 'Shapes to explore'),
            el('div', { class: 'shop-preview-items' }, result.featured.map(product => {
              return el('a', { class: 'shop-preview-item', href: product.url },
                el('img', { src: product.model, alt: product.modelAlt, width: '700', height: '940', loading: 'lazy', decoding: 'async' }),
                el('span', {}, product.title.split(' ')[0]));
            })),
            button(result.products.length === 6 ? 'Explore all six shapes →' : 'Explore the shapes →', () => {
              const target = main.querySelector(`#everyday-title-${config.id}`);
              target.focus({ preventScroll: true });
              target.scrollIntoView({ block: 'start', behavior: 'instant' });
            })) : null,
          showChoiceRecap ? el('div', { class: 'reading-recap' },
            el('section', { class: 'recap', 'aria-label': 'From the choices you made' }, label('From the choices you made'),
              el('ul', {}, result.choices.map(choice => el('li', {}, choice.label))), actionButton('Revisit my choices →', 'edit'))) : null,
          result.products.length ? el('section', { class: 'products', 'aria-labelledby': `everyday-title-${config.id}` },
            el('h2', { id: `everyday-title-${config.id}`, class: 'eyebrow', tabindex: '-1' }, 'For your everyday'), paragraph(readingEverydayCopy(result.everyday, { discoveryCopy }), 'lead'),
            el('div', { class: 'product-groups' }, [['linen', 'French linen'], ['loungewear', 'Loungewear'], ['underwear', 'Underwear'], ['bras', 'Bras'], ['layers', 'Everyday layers']].filter(([category]) => result.products.some(p => p.category === category)).map(([category, title]) =>
              el('section', { class: 'product-group', 'aria-label': title }, el('h2', { class: 'eyebrow' }, title), result.products.filter(p => p.category === category).map(product =>
                el('a', { class: 'product', href: product.url },
                  el('span', { class: 'product-images' },
                    el('img', { class: 'product-model', src: product.model, alt: product.modelAlt, loading: 'lazy', decoding: 'async', width: '700', height: '940' }),
                    product.flatlay !== product.model ? el('img', { class: 'product-flatlay', src: product.flatlay, alt: product.flatlayAlt, loading: 'lazy', decoding: 'async', width: '700', height: '940' }) : null),
                  el('span', { class: 'product-name' }, product.title), el('span', { class: 'small' }, product.shape.join(' · ')),
                  el('span', { class: 'eyebrow' }, 'Shop this shape →'))))))) : null,
          el('div', { class: 'reading-end' }, !showChoiceRecap ? actionButton('Revisit my choices →', 'edit') : null, actionButton('Begin a new reading →', 'restart')));
      }
    }
    document.title = `${pageTitle} | HARA`;
    if (editorial && !invalidLink) applyEditorialPresentation(content, state, {
      composition: config.readingComposition, outcomeId: state.step === 7 ? currentResult().outcomeId : null
    });
    main.replaceChildren(content);
    updateSignupPopup(state.step === 0 && !invalidLink);
    if (focus) {
      main.querySelector('h1').focus({ preventScroll: true });
      root.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  }
  window.addEventListener('popstate', event => {
    const params = new URL(location.href).searchParams;
    invalidLink = false;
    if (params.has('reading')) {
      state = restoreOutcome(params.get('reading'), event.state?.haraJourney);
      if (!state) { invalidLink = true; state = journey.initial(); }
    } else state = journey.restore(event.state?.haraJourney);
    // An invalid saved link does not overwrite the person's existing draft.
    if (!invalidLink) saveStored(storage, state, storageKey);
    render(true);
  }, { signal });
  const progressSaved = invalidLink || remember(true);
  render();
  if (!progressSaved) status.textContent = 'You can keep going. This browser could not save your progress.';
}

}
if (!customElements.get('hara-paired-reading')) {
  customElements.define('hara-paired-reading', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      try { mountReading(this, this.controller.signal); }
      catch (error) { this.querySelector('[data-reading-status]').textContent = this.querySelector('[data-reading-config]').dataset.error; console.error('HARA reading could not open', error); }
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  });
}

})();
